import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import { formatMinor, setStoreCurrency, SUPPORTED_CURRENCIES } from '../utils/money';
// Order additions. Imported from utils, NOT from AdminTakeOrder.js — the two screens
// must stay decoupled, and the queue's only link to Take Order is a route parameter.
import { isAddableOrder, isBillStale } from '../utils/addableOrder';
// In-store ticket/token number. Shared pure resolver so this file's two print builders and
// the queue row cannot disagree with the mobile copies about which number names an order.
import { readStoreTicketNumber, hasStoreTicketNumber, orderDisplayNumber } from '../utils/storeTicket';
// In-app card payment (in-app-card-payment spec). Pure gate resolver, byte-identical in
// logic to the admin-mobile-app copy, so the web and mobile POS queues agree on when the
// "Charge card" button applies (Stripe + PAY_AT_POS + unpaid + tenant opted in). Stripe.js
// itself is loaded LAZILY inside chargeCardInApp so a queue that never charges a card does
// not pull the SDK, and so this module stays importable in jest without it.
import { canChargeCardInApp, canCollectUpiAtStore, squareTerminalCompletion } from '../utils/posPayments';
// The Stripe.js "Charge card" modal. It dynamic-imports @stripe/stripe-js only when
// opened, so importing it here pulls no SDK into the queue bundle.
import StripeChargeCardModal from '../components/StripeChargeCardModal';
// The Razorpay UPI QR "Collect UPI" modal (razorpay-upi-dinein-qr spec). Creates a dynamic
// QR and polls stored status until the webhook settles the order. No SDK, so it adds no
// bundle weight.
import RazorpayUpiQrModal from '../components/RazorpayUpiQrModal';
// POS print paper sizes. The @page rules and the 80mm layout override are shared with the
// mobile print builder, which is an independent copy of this file's logic.
import {
  DEFAULT_PAPER_SIZE,
  normalizePaperSize,
  paperSizeBodyWidth,
  paperSizeOverrideCss,
  paperSizePageCss,
  paperSizeReceiptScaffoldCss,
} from '../utils/paperSize';
// Table party subdivision. Byte-identical with the admin-mobile-app copy, which is what
// makes one order record print the same party text on both surfaces (Property 11).
import {
  hasSharedTable,
  normalizeTableLabel,
  openTicketCountsByTable,
  partyDisplayText,
  partyLabelOf,
  partyNameOf,
  tablePartiesEnabled,
  ticketPartyText,
} from '../utils/partyLabel';
// POS queue status filtering. Byte-identical with the admin-mobile-app copy, so the two
// surfaces cannot disagree about which orders are "active".
import { matchesOrderStatusFilter, isHeldScheduledOrder, formatQueueLoadLine } from '../utils/queueStatusFilter';
// Tenant display timezone. Byte-identical with the admin-mobile-app copy, which is what
// makes one order record print the same placed-at time on both surfaces. Defaults to
// America/New_York, so a tenant with no stored timezone behaves exactly as before.
import {
  formatDateTimeInZone,
  getTenantTimezone,
  isTodayInZone,
  setTenantTimezone,
} from '../utils/tenantTime';
import { useAdminCapability } from '../components/useAdminCapability';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const safeInt = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
};

const computeOwnTax = (subtotalCents, ratePercent) => {
  const subtotal = safeInt(subtotalCents, 0);
  const rate = Number(ratePercent);
  if (!Number.isFinite(rate) || rate <= 0) {
    return { taxCents: 0, totalCents: subtotal };
  }
  const tax = Math.max(0, subtotal) * (rate / 100);
  const taxCents = Math.ceil(tax);
  return { taxCents, totalCents: subtotal + taxCents };
};

// True when the order's stored tax snapshot says prices already include tax.
//
// Under tax-inclusive pricing the server stores subtotal_cents as the GROSS
// (tax-inclusive) amount, tax_cents as the component backed out of it, and
// amount_total_cents == subtotal_cents. Tax-exclusive stores
// amount_total_cents == subtotal_cents + tax_cents - discount_cents.
//
// Read from the per-order snapshot (auGst/gst) rather than live store settings
// so historical orders keep rendering with the regime they were placed under.
// US_SALES orders carry neither block and are always treated as exclusive.
const isTaxInclusiveOrder = (order) => {
  const mode = String(
    order?.auGst?.pricing_mode ?? order?.gst?.pricing_mode ?? ''
  ).trim().toUpperCase();
  return mode === 'TAX_INCLUSIVE';
};

// Discount is not stored on every order, so it is inferred from the identity
// total = subtotal + tax - discount. That identity only holds for tax-EXCLUSIVE
// pricing; applying it to an inclusive order yields a phantom discount exactly
// equal to the tax component (subtotal + tax - subtotal == tax). Skip the
// inference when the order is inclusive and trust the stored value instead.
const resolveDiscountCents = ({
  order,
  subtotalCents,
  taxCents,
  rawTotalCents,
  hasStoredBreakdown,
}) => {
  const storedDiscountCents = safeInt(
    order?.discount_cents
      ?? order?.discount_amount_cents
      ?? order?.customerSubmittedSnapshot?.discount_cents,
    0
  );
  const inclusive = isTaxInclusiveOrder(order);
  const impliedDiscountCents = (hasStoredBreakdown && rawTotalCents > 0 && !inclusive)
    ? Math.max(0, Math.min(subtotalCents, subtotalCents + taxCents - rawTotalCents))
    : 0;
  return Math.max(
    0,
    Math.min(subtotalCents, Math.max(storedDiscountCents, impliedDiscountCents))
  );
};

// Tax label for the breakdown rows. Amounts are unaffected; this is wording only.
const resolveTaxLabel = (order) => {
  const regime = String(order?.taxRegime || '').trim().toUpperCase();
  if (regime === 'IN_GST' || regime === 'AU_GST') return 'GST';
  return 'Tax';
};

const formatMoney = (money, fallbackCents) => {
  if (money && typeof money === 'object') {
    const amt = Number(money.amount);
    const cur = String(money.currency || '').toUpperCase();
    if (Number.isFinite(amt)) {
      // Provider money is in minor units. Use its own currency when we support
      // it, otherwise fall back to the store currency (or raw code if exotic).
      if (cur && SUPPORTED_CURRENCIES[cur]) return formatMinor(amt, cur);
      if (cur) return `${(amt / 100).toFixed(2)} ${cur}`;
      return formatMinor(amt);
    }
  }
  const cents = Number(fallbackCents);
  if (Number.isFinite(cents)) return formatMinor(cents);
  return 'N/A';
};

const formatFulfillmentStatus = (raw, isDelivery = false) => {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return '';
  if (s === 'OPEN') return 'OPEN';

  // Dine-in completion. Recognised BEFORE the delivery/pickup split because it is
  // self-identifying: a dine-in order carries fulfillment_type "pickup" (there is no
  // dine-in fulfillment_type), so isDelivery is false and it would otherwise be
  // mislabelled "PICKUP COMPLETE". Kept above the split so both surfaces resolve it
  // regardless of the isDelivery flag they pass.
  if (s === 'DINE_IN_COMPLETE' || s === 'DINE-IN COMPLETE' || s === 'DINE IN COMPLETE' || s === 'DINEIN_COMPLETE') {
    return 'DINE-IN COMPLETE';
  }

  // Delivery-specific statuses
  if (isDelivery) {
    if (s === 'READY_FOR_DELIVERY' || s === 'READY FOR DELIVERY' || s === 'READY_FOR_PICKUP' || s === 'READY FOR PICKUP' || s === 'READY' || s === 'PREPARED') return 'READY FOR DELIVERY';
    if (s === 'OUT_FOR_DELIVERY' || s === 'OUT FOR DELIVERY') return 'OUT FOR DELIVERY';
    if (s === 'DELIVERED' || s === 'DELIVERY_COMPLETE' || s === 'DELIVERY COMPLETE' || s === 'PICKUP_COMPLETE' || s === 'PICKUP COMPLETE' || s === 'PICKED UP' || s === 'PICKED_UP' || s === 'COMPLETED') return 'DELIVERED';
  } else {
    // Pickup-specific statuses
    if (s === 'READY_FOR_PICKUP' || s === 'READY FOR PICKUP' || s === 'READY' || s === 'PREPARED') return 'READY FOR PICKUP';
    if (s === 'PICKUP_COMPLETE' || s === 'PICKUP COMPLETE' || s === 'PICKED UP' || s === 'PICKED_UP' || s === 'COMPLETED') return 'PICKUP COMPLETE';
  }
  
  if (s === 'CANCELLED' || s === 'CANCELED' || s === 'CANCEL') return 'CANCELLED';
  if (s === 'PROPOSED') return 'OPEN';
  return s;
};

const escapeHtml = (s) => {
  const v = String(s ?? '');
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Compute the SLA band ('green' | 'orange' | 'red' | null) for an order.
// Only OPEN/ACTIVE orders get a band; returns null when the feature is off,
// the config is invalid, the order isn't OPEN, or the timestamp is missing.
const computeSlaBand = (sla, fulfillmentLabel, orderTimestampSeconds, nowMs = Date.now()) => {
  if (!sla || !sla.enabled) return null;
  if (String(fulfillmentLabel || '').trim().toUpperCase() !== 'OPEN') return null;

  const green = Number(sla.greenMinutes);
  const orange = Number(sla.orangeMinutes);
  const red = Number(sla.redMinutes);
  if (
    !Number.isFinite(green) || !Number.isFinite(orange) || !Number.isFinite(red) ||
    !(green > 0 && orange > 0 && red > 0) ||
    !(green < orange && orange < red)
  ) {
    return null;
  }

  const ts = Number(orderTimestampSeconds);
  if (!Number.isFinite(ts) || ts <= 0) return null;

  const elapsedMin = (nowMs / 1000 - ts) / 60;
  if (elapsedMin < green) return 'green';
  if (elapsedMin < orange) return 'orange';
  return 'red';
};

// Bilingual item name for the kitchen ticket (take-order-i18n R10.1-R10.3).
//
// Tamil above, English beneath, so the kitchen can always read the ticket no matter
// which language the order was taken in. The translation is read from the order
// record's own `name_i18n`, stamped server-side at creation — never looked up live —
// so a later menu rename cannot rewrite a historical ticket.
//
// When there is no Tamil name (every pre-existing order, and every single-language
// tenant) this returns the escaped English name and NOTHING else, so the emitted
// markup is character-identical to pre-feature (R10.2, R1.7).
// Plain-text bilingual item name for the ON-SCREEN queue (take-order-i18n R10.4).
// Same rule as the printed ticket, rendered on one line: "தமிழ் (English)".
// Returns the bare English name when there is no translation, so pre-existing
// orders and single-language tenants render exactly as before (R1.8).
const posItemNameText = (it) => {
  const en = String(it?.name || it?.ProductName || 'Item');
  const map = it && typeof it.name_i18n === 'object' && it.name_i18n !== null && !Array.isArray(it.name_i18n)
    ? it.name_i18n
    : null;
  const ta = map && typeof map.ta === 'string' ? map.ta.trim() : '';
  return ta ? `${ta} (${en})` : en;
};

// TEMPORARILY ENGLISH-ONLY (owner request, 2026-08). The Tamil line was being printed
// on documents for orders the waiter took in English, because `name_i18n` is stamped
// from the CATALOG's configured translation regardless of which language the order
// was taken in -- it is not a record of what language the waiter used. Bilingual
// print output is disabled here until that is revisited; every print document goes
// back to plain English, byte-identical to a tenant with no translations at all.
// The Tamil name is still STORED on the line (`name_i18n` is untouched) and the
// on-screen queue text (`posItemNameText`) is UNCHANGED, so nothing here affects data
// or the staff-facing screen -- only the physical/PDF documents built by
// posItemNameHtml's callers.
const posItemNameHtml = (it, englishName) => escapeHtml(englishName);

// Addition chit for the kitchen (order-additions R8.1-R8.5).
//
// A SEPARATE builder rather than a new mode of buildPosOrderReceiptHtml, deliberately.
// That function is ~400 lines with two existing item-rendering paths that must stay
// character-identical for orders never added to (Property 1); threading a third mode
// through it risks the two paths that already work. This one is small, has no branches
// that touch them, and produces the same bilingual item markup by reusing
// posItemNameHtml.
//
// Lists ONLY the batch's lines and carries NO totals — the kitchen never needs money,
// and a chit showing an order total invites someone to treat it as a bill. Reprinting
// the whole ticket is how kitchens double-cook, so this is a delta by design.
const buildAdditionChitHtml = (order, amendment, { businessName } = {}) => {
  const orderNumber = String(order?.order_number || '').trim();
  // What the kitchen is called by. The token when the tenant issues one, the order number
  // otherwise -- resolved through the shared helper so this chit, the kitchen ticket and
  // the queue row cannot disagree about which number identifies this order.
  const displayNumber = orderDisplayNumber(order) || orderNumber;
  const displayLabel = hasStoreTicketNumber(order) ? 'Ticket' : 'Order';
  const lines = Array.isArray(amendment?.lines) ? amendment.lines : [];

  const orderType = String(order?.orderType || order?.customerSubmittedSnapshot?.orderType || '')
    .trim()
    .toUpperCase();
  const tableLabel = String(order?.tableLabel || order?.customerSubmittedSnapshot?.tableLabel || '').trim();
  const partyLabel = String(order?.partyLabel || order?.customerSubmittedSnapshot?.partyLabel || '').trim();
  const serverName = String(amendment?.byName || '').trim();
  const target = [
    orderType === 'DINE_IN' ? (tableLabel ? `TABLE ${tableLabel}` : 'DINE-IN') : 'TO-GO',
    partyLabel ? `GROUP ${partyLabel}` : '',
  ]
    .filter(Boolean)
    .join(' — ');

  const addedAt = amendment?.at ? formatDateTimeInZone(amendment.at) : '';

  // Item lines are resolved through the SAME helper the kitchen ticket uses, so the
  // same order record renders the same bilingual text on both documents.
  const itemRows = lines
    .slice(0, 50)
    .map((it) => {
      const itemName = String(it?.name || it?.ProductName || 'Item').trim();
      const qtyNum = Number(it?.Quantity ?? it?.quantity ?? 1);
      const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
      const notes = String(it?.item_notes || it?.itemNotes || '').trim();
      return `
        <tr>
          <td class="qty">${escapeHtml(qty)} ×</td>
          <td>
            ${posItemNameHtml(it, itemName)}
            ${notes ? `<span class="item-notes">${escapeHtml(notes)}</span>` : ''}
          </td>
        </tr>
      `;
    })
    .join('');

  return `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Order change ${escapeHtml(displayNumber)}</title>
        <style>
          @page { size: 3.5in auto; margin: 0.2in; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 8px; color: #000; }
          .banner { border: 3px solid #000; padding: 6px; text-align: center; }
          .banner .kind { font-size: 20px; font-weight: 900; letter-spacing: 0.06em; }
          .banner .target { font-size: 22px; font-weight: 900; margin-top: 2px; }
          .meta { margin-top: 8px; font-size: 12px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          td { padding: 8px 4px; font-size: 16px; border-bottom: 1px dashed #000; vertical-align: top; }
          .qty { font-weight: 900; width: 46px; white-space: nowrap; }
          .item-notes { display: block; margin-top: 4px; font-size: 13px; font-weight: 700; }
          /* Bilingual item names, identical rule to the kitchen ticket. */
          .item-name-ta { font-weight: 800; }
          .item-name-en { font-weight: 700; font-size: 0.85em; opacity: 0.75; margin-top: 2px; }
          .foot { margin-top: 10px; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="banner">
          <div class="kind">*** ADDED ITEMS ***</div>
          ${target ? `<div class="target">${escapeHtml(target)}</div>` : ''}
        </div>
        <div class="meta">
          <div>${escapeHtml(displayLabel)} ${escapeHtml(displayNumber || '—')}</div>
          ${addedAt ? `<div>Added ${escapeHtml(addedAt)}</div>` : ''}
          ${serverName ? `<div>Added by ${escapeHtml(serverName)}</div>` : ''}
        </div>
        <table>
          <tbody>
            ${itemRows || '<tr><td colspan="2">No added items found</td></tr>'}
          </tbody>
        </table>
        <div class="foot">
          These items are IN ADDITION to the original ticket. Do not re-make the whole order.
          ${businessName ? `<div>${escapeHtml(businessName)}</div>` : ''}
        </div>
      </body>
    </html>
  `;
};

// Exported for unit testing only (receipt-content regression). The component uses it
// directly; the named export lets a test invoke the real builder and assert the printed
// HTML rather than duplicate the layout in a fixture.
export const buildPosOrderReceiptHtml = (
  order,
  {
    businessName,
    logoUrl,
    address,
    phone: businessPhone,
    website,
    // Content type: 'ticket' = kitchen order-details sheet with NO prices (the old
    // orderDetailsOnly=true output); 'receipt' = itemized bill with prices/totals.
    // `orderDetailsOnly` is still accepted as a deprecated alias so any un-updated
    // caller keeps working: true -> 'ticket', false -> 'receipt'.
    contentType,
    orderDetailsOnly,
    // Paper size, independent of content type. One of the values in utils/paperSize.js:
    // 'half_letter' (5.5x8.5), 'a4', or 'thermal_80mm' (80 mm receipt roll). Defaults
    // half_letter, which is what the old orderDetailsOnly toggle always produced.
    paperSize = DEFAULT_PAPER_SIZE,
    // Table party subdivision gate (R1.9, R7.4). Passed IN rather than inferred from
    // the order record, so disabling the tenant setting fully reverts printed output —
    // that is what makes the toggle govern the whole feature rather than most of it.
    // Defaults false, so any caller that has not been updated prints exactly as before.
    partiesEnabled = false,
    // Tips enabled (pos-terminal-tips). Drives the unpaid customer bill's Tip:/Total:
    // write-in area. Defaults false, so a non-tipping tenant's receipt is unchanged.
    tipsEnabled = false,
  } = {}
) => {
  // Resolve content type from the explicit arg, falling back to the deprecated
  // boolean. 'ticket' is the kitchen sheet (no prices); anything else is 'receipt'.
  const resolvedContentType = contentType
    ? (String(contentType).trim().toLowerCase() === 'ticket' ? 'ticket' : 'receipt')
    : (orderDetailsOnly ? 'ticket' : 'receipt');
  // Normalized through the shared allowlist, so an unknown value can never reach the
  // @page rule below.
  const resolvedPaperSize = normalizePaperSize(paperSize);
  // Content is chosen separately from size. For the two SHEET sizes the @page rule is the
  // only thing size controls and the body width tracks the page, so the itemized grid fills
  // it. The 80 mm roll additionally needs a layout override -- see `paperOverrideCss`.
  const pageCss = paperSizePageCss(resolvedPaperSize);
  // Appended AFTER the base rules in the style block, so it wins. Empty string for both
  // sheet sizes, which is what keeps their output byte-identical to before 80 mm existed.
  //
  // Necessary because the base ticket layout is a two-column grid with 15-16px type and
  // fixed 36px/88px table columns, all sized for a 139 mm sheet. On the roll's ~72 mm of
  // printable width that wraps into an unreadable mess, so correct paper alone would still
  // produce a useless chit.
  const paperOverrideCss = paperSizeOverrideCss(resolvedPaperSize);
  const orderNumber = String(order?.order_number || '').trim();
  // In-store token, when the tenant issues one. Resolved through the shared helper so the
  // kitchen ticket, the itemized bill, the addition chit and the queue row all name this
  // order the same way. `orderNumber` stays available and unchanged for anything that
  // needs the real identifier.
  const ticketNumber = readStoreTicketNumber(order);
  const displayNumber = ticketNumber || orderNumber;
  const displayLabel = ticketNumber ? 'Ticket Number' : 'Order Number';
  const placedAt = formatDateTimeInZone(order?.order_timestamp);
  // Date and time as separate fields on the printed ticket. Same instant and same zone as
  // `placedAt` above -- one ticket must not carry two different days -- so these read the
  // resolved tenant zone rather than a second hardcoded Eastern. Guards unchanged.
  const orderDate = (() => {
    try {
      const n = Number(order?.order_timestamp);
      const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(order?.order_timestamp);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toLocaleDateString('en-US', { timeZone: getTenantTimezone() });
    } catch {
      return '';
    }
  })();
  const orderTime = (() => {
    try {
      const n = Number(order?.order_timestamp);
      const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(order?.order_timestamp);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toLocaleTimeString('en-US', { timeZone: getTenantTimezone() });
    } catch {
      return '';
    }
  })();
  const name = String(order?.customer_name || '').trim();
  const phone = String(order?.customer_phone || '').trim();
  const email = String(order?.customer_email || '').trim();
  const notes = String(order?.customerSubmittedSnapshot?.notes || '').trim();
  const items = Array.isArray(order?.customerSubmittedSnapshot?.items) ? order.customerSubmittedSnapshot.items : [];

  // Server Order Entry (staff) — additive, gated on presence. Legacy/customer
  // orders have no orderType, so these resolve to empty and render nothing.
  const orderType = String(order?.orderType || order?.customerSubmittedSnapshot?.orderType || '').trim().toUpperCase();
  const tableLabel = String(order?.tableLabel || order?.customerSubmittedSnapshot?.tableLabel || '').trim();
  const serverName = String(order?.serverName || order?.customerSubmittedSnapshot?.serverName || '').trim();
  // Table party subdivision (R7.1, R7.2, R7.3, R7.5). Resolved through the SHARED
  // resolver, which the mobile builder also uses -- that is what makes one order record
  // print the same party text on both surfaces (Property 11) instead of two independent
  // 5,000-line files drifting.
  //
  // The identifier stays LATIN even in a Tamil session (R9.3): it is read aloud off this
  // chit by a server, a cook and a cashier, next to Arabic-numeral table numbers.
  const partyText = partiesEnabled ? partyDisplayText(order) : '';
  const partyTableText = partiesEnabled ? ticketPartyText(order) : '';
  const orderTypeText =
    orderType === 'DINE_IN'
      ? (tableLabel
        ? `DINE-IN · Table ${tableLabel}${partyText ? ` · ${partyText}` : ''}`
        : 'DINE-IN')
      : orderType === 'TO_GO'
        ? 'TO-GO'
        : '';

  const safeInt = (v, fallback = 0) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    try {
      return Math.trunc(n);
    } catch {
      return fallback;
    }
  };

  // Delivery information
  const isDelivery = String(order?.fulfillment_type || '').trim() === 'delivery';
  const deliveryAddress = order?.delivery_address;
  const deliveryInstructions = String(order?.delivery_instructions || '').trim();
  const deliveryFeeCents = safeInt(order?.delivery_fee_cents, 0);

  // Otter information
  const isOtterOrder = String(order?.order_source || '').trim() === 'otter';
  const deliveryChannel = String(order?.delivery_channel || '').trim();
  const otterOrderId = String(order?.otter_order_id || '').trim();
  const requestedDeliveryTime = order?.requested_delivery_time;
  const requestedDeliveryTimeFormatted = requestedDeliveryTime ? formatDateTimeInZone(requestedDeliveryTime) : '';

  const orderSubtotalCents = safeInt(order?.subtotal_cents, 0);
  const orderTaxCents = safeInt(order?.tax_cents, 0);
  const hasStoredBreakdown = Number.isFinite(Number(order?.subtotal_cents)) && Number.isFinite(Number(order?.tax_cents));
  const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(order?.amount_total_cents, 0);
  const taxCents = hasStoredBreakdown ? orderTaxCents : 0;
  const receiptInclusive = isTaxInclusiveOrder(order);
  // Inclusive: tax already sits inside the subtotal, so adding it again would
  // overstate the total. Exclusive keeps the original subtotal + tax.
  const totalCents = hasStoredBreakdown
    ? (receiptInclusive ? subtotalCents : subtotalCents + taxCents)
    : safeInt(order?.amount_total_cents, 0);

  const subtotalText = formatMoney(null, subtotalCents);
  const taxText = formatMoney(null, taxCents);
  const totalText = formatMoney(null, totalCents);

  // Customer-receipt additions (mirror the mobile 80mm receipt). tip is printed only on a
  // PAID receipt when > 0; an unpaid bill shows a Tip:/Total: write-in area instead (Flow 2).
  const tipCents = safeInt(order?.tip_cents, 0);
  const grandTotalCents = totalCents + (tipCents > 0 ? tipCents : 0);
  const tipText = formatMoney(null, tipCents);
  const grandTotalText = formatMoney(null, grandTotalCents);
  const isPaidReceipt = String(order?.payment_status || '').trim().toLowerCase() === 'paid';
  const receiptTipsEnabled = Boolean(tipsEnabled);
  const showTipWriteIn = !isPaidReceipt && receiptTipsEnabled;
  // GST vs sales tax — label only; the amount is unchanged.
  const taxLabelText = `${resolveTaxLabel(order)}${receiptInclusive ? ' (incl.)' : ''}`;
  // Short payment-method word for the "Paid ·" line.
  const paidMethod = (() => {
    const src = String(order?.posStripePaymentSource || '').trim().toLowerCase();
    if (src === 'terminal_reader') return 'Reader';
    const prov = String(order?.posProvider || '').trim().toUpperCase();
    if (prov === 'STRIPE') return 'Card';
    if (prov === 'SQUARE') return 'Square';
    if (prov === 'CLOVER') return 'Clover';
    return '';
  })();
  // Discount, mirroring the mobile builder (web previously omitted it).
  const discountCents = resolveDiscountCents({
    order,
    subtotalCents,
    taxCents,
    rawTotalCents: safeInt(order?.amount_total_cents, 0),
    hasStoredBreakdown,
  });
  const discountText = formatMoney(null, discountCents);

  const effectiveBusinessName = String(businessName || '').trim() || 'YOUR RESTAURANT NAME';
  // Address/phone for the receipt header. The builder signature already accepts address/
  // phone; also fall back to the order's LOCATION fields. Omitted when absent.
  const effectiveAddress = String(address || order?.location_address || '').trim();
  const effectivePhone = String(businessPhone || order?.location_phone || '').trim();
  const effectiveLogoUrl = String(logoUrl || '').trim();

  const detailsOnly = resolvedContentType === 'ticket';
  if (detailsOnly) {
    return `
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Order ${escapeHtml(displayNumber || '')}</title>
          <style>
            ${pageCss}
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #111; font-size: 15px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
            .cell { display: flex; gap: 8px; align-items: baseline; }
            .label { font-weight: 900; white-space: nowrap; }
            .fill { flex: 1; border-bottom: 2px solid #111; height: 0.95em; }
            .hr { border-top: 2px solid #111; margin: 16px 0; }
            .section-title { font-size: 18px; font-weight: 900; margin: 0 0 10px 0; }
            table { width: 100%; border-collapse: collapse; }
            th { text-align: left; padding: 10px 6px; font-size: 15px; border-bottom: 2px solid #111; }
            td { padding: 12px 6px; font-size: 16px; border-bottom: 1px solid #222; vertical-align: top; }
            .col-num { width: 36px; }
            .col-qty { width: 88px; text-align: right; }
            .item-notes { display: block; margin-top: 6px; font-weight: 700; }
            /* Bilingual item names (take-order-i18n R10.1). Tamil leads at full
               weight; the English name sits beneath it, smaller but still clearly
               legible, so a cook who reads only one of the two is never stuck. */
            .item-name-ta { font-weight: 800; }
            .item-name-en { font-weight: 700; font-size: 0.85em; opacity: 0.75; margin-top: 2px; }
            .write-line { border-bottom: 2px solid #111; height: 22px; margin-top: 10px; }
            /* LAST, so it overrides everything above. Empty for both sheet sizes. */
            ${paperOverrideCss}
          </style>
        </head>
        <body>
          <div class="grid">
            <div class="cell"><span class="label">${escapeHtml(displayLabel)}:</span><span class="fill">${escapeHtml(displayNumber || '')}</span></div>
            <div class="cell"><span class="label">Customer Name:</span><span class="fill">${escapeHtml(name || '')}</span></div>
          </div>

          ${orderTypeText ? `
            <div class="cell" style="margin-top:12px;"><span class="label">Order Type:</span><span class="fill" style="font-weight:900">${escapeHtml(orderTypeText)}</span></div>
          ` : ''}
          ${serverName ? `
            <div class="cell" style="margin-top:12px;"><span class="label">Order Taken By:</span><span class="fill" style="font-weight:900">${escapeHtml(serverName)}</span></div>
          ` : ''}

          ${isOtterOrder ? `
            <div class="hr"></div>
            <div style="padding: 12px; border: 3px solid #FF6B35; background: #FFF4ED;">
              <div style="font-size: 18px; font-weight: 900; margin-bottom: 8px; color: #FF6B35;">🦦 OTTER ORDER${deliveryChannel ? ` - ${escapeHtml(deliveryChannel)}` : ''}</div>
              ${otterOrderId ? `<div style="font-weight: 700; margin-top: 4px;">Otter Order ID: ${escapeHtml(otterOrderId)}</div>` : ''}
              ${requestedDeliveryTimeFormatted ? `<div style="font-weight: 700; margin-top: 4px;">Requested Delivery: ${escapeHtml(requestedDeliveryTimeFormatted)}</div>` : ''}
            </div>
          ` : ''}

          ${isDelivery && deliveryAddress ? `
            <div class="hr"></div>
            <div style="padding: 12px; border: 3px solid #111; background: #f3f4f6;">
              <div style="font-size: 18px; font-weight: 900; margin-bottom: 8px;">🚚 DELIVERY ORDER</div>
              <div style="font-weight: 800; margin-top: 6px;">
                ${escapeHtml(deliveryAddress.street)}${deliveryAddress.apt_unit ? `, ${escapeHtml(deliveryAddress.apt_unit)}` : ''}
              </div>
              <div style="font-weight: 800;">
                ${escapeHtml(deliveryAddress.city)}, ${escapeHtml(deliveryAddress.state)} ${escapeHtml(deliveryAddress.zip)}
              </div>
              ${deliveryInstructions ? `
                <div style="margin-top: 8px; font-weight: 700;">
                  <span style="font-weight: 900;">Instructions:</span> ${escapeHtml(deliveryInstructions)}
                </div>
              ` : ''}
              ${deliveryFeeCents > 0 ? `
                <div style="margin-top: 8px; font-weight: 900;">
                  Delivery Fee: ${escapeHtml(formatMoney(null, deliveryFeeCents))}
                </div>
              ` : ''}
            </div>
          ` : ''}

          <div class="hr"></div>

          <div class="section-title">ITEMIZED ORDER</div>
          <table>
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th>Item Name (Include Special Instructions)</th>
                <th class="col-qty">Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${(items || []).slice(0, 50).map((it, idx) => {
                const itemName = String(it?.name || it?.ProductName || 'Item').trim();
                const qtyRaw = it?.quantity ?? it?.Quantity ?? 1;
                const qtyNum = Number(qtyRaw);
                const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
                const itemNotes = String(it?.item_notes || it?.itemNotes || '').trim();
                return `
                  <tr>
                    <td class="col-num">${escapeHtml(idx + 1)}</td>
                    <td>
                      ${posItemNameHtml(it, itemName)}
                      ${itemNotes ? `<span class="item-notes">${escapeHtml(itemNotes)}</span>` : ''}
                    </td>
                    <td class="col-qty">${escapeHtml(qty)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div style="margin-top: 28px;">
            <div class="section-title">ADDITIONAL NOTES / SPECIAL REQUESTS</div>
            ${notes ? `<div style="font-size:16px; font-weight:800; white-space: pre-wrap; margin-top: 10px;">${escapeHtml(notes)}</div>` : '<div class="write-line"></div>'}
          </div>
        </body>
      </html>
    `;
  }

  // Per-line price = qty × (unit base + modifier delta). Same field resolver salesMetrics
  // uses, so the printed line total matches revenue reporting. Only shown when > 0.
  const receiptItemRows = (items || []).slice(0, 100).map((it) => {
    const itemName = String(it?.name || it?.ProductName || 'Item').trim();
    const qtyRaw = it?.quantity ?? it?.Quantity ?? 1;
    const qtyNum = Number(qtyRaw);
    const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
    const unitBase = safeInt(it?.unit_price_cents ?? it?.unitPriceCents ?? it?.price_cents ?? it?.priceCents, 0);
    const unitMod = safeInt(it?.modifier_price_cents ?? it?.modifierPriceCents, 0);
    const lineCents = (unitBase + unitMod) * qty;
    const priceHtml = lineCents > 0 ? escapeHtml(formatMoney(null, lineCents)) : '';
    const itemNotes = String(it?.item_notes || it?.itemNotes || '').trim();
    // Reference layout: name (left, wraps) | qty (right) | price (right). Name uses the
    // shared Tamil/English renderer.
    return `
      <div class="r-item">
        <span class="r-name">${posItemNameHtml(it, itemName)}</span>
        <span class="r-qty">${escapeHtml(qty)}</span>
        <span class="r-price">${priceHtml}</span>
      </div>
      ${itemNotes ? `<div class="r-note">${escapeHtml(itemNotes)}</div>` : ''}
    `;
  }).join('');

  return `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
        <title>${escapeHtml(effectiveBusinessName)}</title>
        <style>
          * { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          ${paperSizeReceiptScaffoldCss(resolvedPaperSize)}
          html { margin: 0; padding: 0; }
          /* 80mm-native customer receipt. Monospace so the price column lines up; pure black
             (thermal fades grays); centered store header; full-width dashed rules, no boxes. */
          body { 
            font-family: "Menlo", "Consolas", "Courier New", monospace;
            margin: 0 auto; 
            padding: 8px 6px; 
            color: #000; 
            font-size: 12px; 
            line-height: 1.35;
            width: ${paperSizeBodyWidth(resolvedPaperSize)};
            max-width: ${paperSizeBodyWidth(resolvedPaperSize)};
            box-sizing: border-box;
          }
          .center { text-align: center; }
          .rule-heavy { border-top: 3px double #000; margin: 6px 0; }
          .rule-light { border-top: 1px dashed #000; margin: 6px 0; }
          .store { font-size: 17px; font-weight: 800; letter-spacing: 0.02em; }
          .meta { font-size: 12px; }
          .meta .mrow { display: flex; justify-content: space-between; gap: 8px; }
          .colhead { display: flex; font-weight: 800; font-size: 12px; }
          .colhead .h-item { flex: 1; }
          .colhead .h-qty { width: 44px; text-align: right; }
          .colhead .h-price { width: 84px; text-align: right; }
          .r-item { display: flex; align-items: baseline; font-weight: 700; margin-top: 4px; }
          .r-item .r-name { flex: 1; word-break: break-word; }
          .r-item .r-qty { width: 44px; text-align: right; }
          .r-item .r-price { width: 84px; text-align: right; white-space: nowrap; }
          .r-note { margin-left: 10px; font-size: 11px; font-weight: 400; }
          .item-name-ta { font-weight: 800; }
          .item-name-en { font-weight: 700; font-size: 0.85em; opacity: 0.85; margin-top: 2px; }
          .totals { margin-top: 4px; }
          .totals .trow { display: flex; justify-content: flex-end; gap: 12px; margin-top: 3px; }
          .totals .amt { min-width: 84px; text-align: right; font-weight: 700; white-space: nowrap; }
          .grand { font-size: 15px; font-weight: 800; }
          .writein { margin-top: 8px; }
          .writein .wrow { display: flex; align-items: baseline; gap: 8px; margin-top: 8px; }
          .writein .wlbl { min-width: 52px; font-weight: 700; }
          .writein .wfill { flex: 1; border-bottom: 1px solid #000; height: 1em; }
          .footer { margin-top: 6px; font-size: 12px; font-weight: 700; }
          .footer .thin { font-weight: 400; font-size: 11px; }
          /* LAST, so it overrides everything above. Empty string for both sheet sizes. */
          ${paperOverrideCss}
        </style>
      </head>
      <body>
        <div class="rule-heavy"></div>
        <div class="center store">${escapeHtml(effectiveBusinessName)}</div>
        <div class="rule-heavy"></div>

        <div class="meta">
          <div class="mrow">
            <span><strong>${escapeHtml(displayLabel)} #${escapeHtml(displayNumber || '')}</strong></span>
            <span>${escapeHtml(placedAt || '')}</span>
          </div>
          ${orderTypeText ? `<div>Type: ${escapeHtml(orderTypeText)}</div>` : ''}
          ${partyTableText ? `<div>${escapeHtml(partyTableText)}</div>` : ''}
          ${name ? `<div>${escapeHtml(name)}</div>` : ''}
        </div>

        <div class="rule-light"></div>
        <div class="colhead"><span class="h-item">ITEM</span><span class="h-qty">QTY</span><span class="h-price">PRICE</span></div>
        <div class="rule-light"></div>

        <div>${receiptItemRows || `<div class="r-item"><span class="r-name">No items</span></div>`}</div>

        <div class="rule-light"></div>
        <div class="totals">
          <div class="trow"><span>Subtotal:</span><span class="amt">${escapeHtml(subtotalText)}</span></div>
          ${discountCents ? `<div class="trow"><span>Discount:</span><span class="amt">-${escapeHtml(discountText)}</span></div>` : ''}
          ${isDelivery && deliveryFeeCents > 0 ? `<div class="trow"><span>Delivery:</span><span class="amt">${escapeHtml(formatMoney(null, deliveryFeeCents))}</span></div>` : ''}
          <div class="trow"><span>${escapeHtml(taxLabelText)}:</span><span class="amt">${escapeHtml(taxText)}</span></div>
          ${isPaidReceipt && tipCents > 0 ? `<div class="trow"><span>Tip:</span><span class="amt">${escapeHtml(tipText)}</span></div>` : ''}
        </div>
        <div class="rule-light"></div>
        <div class="totals"><div class="trow grand"><span>TOTAL:</span><span class="amt">${escapeHtml(isPaidReceipt && tipCents > 0 ? grandTotalText : totalText)}</span></div></div>

        ${showTipWriteIn ? `
          <div class="rule-light"></div>
          <div class="writein">
            <div class="wrow"><span class="wlbl">Tip</span><span class="wfill"></span></div>
            <div class="wrow"><span class="wlbl">Total</span><span class="wfill"></span></div>
          </div>
        ` : ''}

        ${notes ? `<div class="rule-light"></div><div class="meta"><strong>Notes:</strong> ${escapeHtml(notes)}</div>` : ''}

        <div class="rule-heavy"></div>
        <div class="center footer">Thank You!<div class="thin">We appreciate your business.</div></div>
      </body>
    </html>
  `;
};

// An iOS-family device, including iPadOS, which reports a desktop "Macintosh" UA
// but still exposes touch points. Mirrors the check already used for the Square POS
// deep link below.
const isIosDevice = () => {
  try {
    const ua = String(navigator?.userAgent || '');
    if (/iphone|ipad|ipod/i.test(ua)) return true;
    return /macintosh/i.test(ua) && Number(navigator?.maxTouchPoints || 0) > 1;
  } catch {
    return false;
  }
};

// Branded UA tokens for the iOS browsers that are NOT Safari: Chrome, Firefox, Edge,
// Opera, the Google app, DuckDuckGo, Brave.
const IOS_NON_SAFARI_UA_TOKENS = [
  /CriOS/i, /FxiOS/i, /EdgiOS/i, /OPiOS/i, /OPT\//i, /GSA\//i, /DuckDuckGo/i, /Brave/i,
];

// TRUE only for an iOS browser that is not Safari, which cannot print a subframe.
//
// WHY. On iOS every browser renders in WKWebView, but only Safari is WebKit's
// privileged embedder and can route `print()` through WebKit's own print path, which
// knows which frame asked. Every other iOS browser is an ordinary app hosting a
// WKWebView; it services a print request by attaching a print formatter to the WHOLE
// web view, and Apple's UIViewPrintFormatter has no notion of a target subframe. So
// `iframe.contentWindow.print()` there prints the top-level page — the entire admin
// portal — instead of the receipt.
//
// Deliberately an ALLOWLIST of known-broken browsers rather than "iOS and not
// Safari": anything unrecognized keeps today's iframe path. iPad Safari and every
// desktop browser print correctly through the iframe today and must not change.
const needsTopLevelPrintWindow = () => {
  if (!isIosDevice()) return false;
  const ua = String(navigator?.userAgent || '');
  return IOS_NON_SAFARI_UA_TOKENS.some((token) => token.test(ua));
};

// Resolve once the document's images have loaded, or after `timeoutMs`, whichever is
// first. The receipt carries the tenant logo, and printing before it decodes yields a
// receipt with a blank brand area.
//
// Shared by both print paths. Note this defers print() past the user's tap, which is
// already true of the existing iframe path and is fine: on iOS the gesture gates
// window.open, not print().
const waitForDocumentImages = (d, timeoutMs = 2000) => {
  try {
    const imgs = Array.from(d?.images || []);
    const pending = imgs.filter((img) => img && !img.complete);
    if (pending.length === 0) return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      let remaining = pending.length;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const onDone = () => {
        remaining -= 1;
        if (remaining <= 0) finish();
      };

      for (const img of pending) {
        try {
          img.addEventListener('load', onDone, { once: true });
          img.addEventListener('error', onDone, { once: true });
        } catch {
          onDone();
        }
      }

      window.setTimeout(finish, Math.max(0, Number(timeoutMs) || 0));
    });
  } catch {
    return Promise.resolve();
  }
};

// Print by writing the receipt into a NEW TOP-LEVEL window, which is the only thing a
// WKWebView-hosting browser can print correctly. Same sequence as the report print in
// AdminOrderReports.js, which is verified working on iPad Chrome.
//
// The window is left open on purpose. Closing it can cancel an in-flight print job,
// and if the print sheet is dismissed the receipt is still on screen so staff can use
// the browser's own Print action.
const printReceiptInTopLevelWindow = (html) => {
  // Must run synchronously inside the tap handler or iOS blocks the popup.
  const win = window.open('', '_blank');
  if (!win) {
    throw new Error(
      'Your browser blocked the print window. Allow pop-ups for this site, or print from Safari.'
    );
  }

  const doc = win.document;
  doc.open();
  doc.write(String(html || ''));
  doc.close();

  waitForDocumentImages(doc, 2500).finally(() => {
    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch {
        // The receipt is still displayed in the new tab; staff can print manually.
      }
    }, 50);
  });
};

// Print via a hidden iframe. Correct on desktop browsers and on iPad Safari, all of
// which scope print() to the calling frame. UNCHANGED behavior.
const printReceiptInHiddenIframe = (html) => {
  if (typeof document === 'undefined') throw new Error('Printing is not available');
  const frame = document.createElement('iframe');
  frame.setAttribute('title', 'print');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const doc = frame.contentDocument || win?.document;
  if (!win || !doc) {
    try {
      document.body.removeChild(frame);
    } catch {
    }
    throw new Error('Printing is not available');
  }

  doc.open();
  doc.write(String(html || ''));
  doc.close();

  waitForDocumentImages(doc, 2500).finally(() => {
    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } finally {
        window.setTimeout(() => {
          try {
            document.body.removeChild(frame);
          } catch {
          }
        }, 500);
      }
    }, 50);
  });
};

// Print a receipt, choosing the mechanism the current browser can actually honor.
//
// `allowNewWindow` MUST be true only when this is called synchronously from a user
// gesture, because opening a window is gesture-gated on iOS. Auto-print runs from a
// poll timer, so it passes false and keeps the iframe path — a blocked popup there
// would print nothing at all, and auto-print on a browser tab is not the supported
// setup for unattended printing (the mobile admin app is).
const printReceiptHtml = (html, { allowNewWindow = false } = {}) => {
  if (allowNewWindow && needsTopLevelPrintWindow()) {
    return printReceiptInTopLevelWindow(html);
  }
  return printReceiptInHiddenIframe(html);
};

const presetKeyToLabel = (key) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return '1 day (today)';
  if (k === '7d') return '7 days';
  if (k === '1m') return '1 month';
  if (k === '6m') return '6 months';
  if (k === 'ytd') return 'this year';
  if (k === 'all') return 'ALL';
  if (k === 'custom') return 'CUSTOM';
  return key ? String(key) : '7 days';
};

const presetKeyToDays = (key, customDays) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return 1;
  if (k === '7d') return 7;
  if (k === '1m') return 31;
  if (k === '6m') return 183;
  if (k === 'all') return 365;
  if (k === 'ytd') {
    try {
      const now = new Date();
      const jan1 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      const diffMs = now.getTime() - jan1.getTime();
      const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      return Math.min(365, days);
    } catch {
      return 365;
    }
  }
  if (k === 'custom') {
    const n = Number(customDays);
    if (Number.isFinite(n) && n > 0) return Math.min(365, Math.floor(n));
    return 7;
  }
  return 7;
};

export default function AdminPosQueue() {
  const navigate = useNavigate();

  // Whether this caller may create/extend orders, which gates the "+ Add items" deep
  // link (order-additions R2.13). Separate from POS Queue access: a cashier granted
  // POS Queue but not Take Order runs this screen and must NOT see the link.
  // Convenience gating only — the server enforces Take Order: Edit independently.
  const takeOrderCapability = useAdminCapability();
  // canEditTab, NOT canEdit: the per-tab permission map is `{}` for Admin and
  // Superadmin (their access is role-based), so canEdit('Take Order') is always
  // false for them and this link would never render for an admin — which is
  // exactly the bug that hid it until now.
  const takeOrderCanEdit = takeOrderCapability.loaded && !takeOrderCapability.error
    ? takeOrderCapability.canEditTab('Take Order')
    : false;
  const statusRef = useRef(null);
  const autoRefreshInFlightRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // THE LOAD COUNT IS DERIVED AT RENDER, NOT BAKED INTO A MESSAGE STRING.
  //
  // The status line used to be built inside the fetch as
  // `Loaded ${nextOrders.length} order(s).` -- the size of the array the SERVER returned.
  // The list underneath renders `displayOrders`, which is that array after the client-side
  // status / search / today narrowing. With any filter active the two disagreed, so the
  // banner reported a number that matched nothing on screen: reported 18 while two
  // DINE-IN COMPLETE tickets were listed.
  //
  // Storing only the server-side facts here and rendering the visible count from
  // `displayOrders.length` makes the number equal to the rows by construction. It also
  // self-corrects when the operator changes a filter WITHOUT refetching, which the old
  // frozen string could never do.
  const [queueLoadInfo, setQueueLoadInfo] = useState(null);

  const [lookbackDays, setLookbackDays] = useState(7);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('any');
  const [orderStatusFilter, setOrderStatusFilter] = useState('active');
  const [salesChannelFilter, setSalesChannelFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [dateMode, setDateMode] = useState('preset');
  const [presetKey, setPresetKey] = useState('7d');
  const [customDays, setCustomDays] = useState('7');
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [newOrderUntil, setNewOrderUntil] = useState({});
  const [cursor, setCursor] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [detailsOrder, setDetailsOrder] = useState(null);

  const [posTaxEnabled, setPosTaxEnabled] = useState(false);
  const [posTaxCollectionMode, setPosTaxCollectionMode] = useState('PROVIDER');
  const [posTaxRatePercent, setPosTaxRatePercent] = useState(null);

  const [autoPrintNewOrdersEnabled, setAutoPrintNewOrdersEnabled] = useState(false);
  // Auto-print the customer RECEIPT when an order is marked paid (pay-at-store / counter).
  // Separate from autoPrintNewOrders (which fires the KITCHEN ticket on new-order arrival).
  // Default OFF; a tenant opts in. Read from /admin/general like the other print settings.
  const [autoPrintReceiptOnPayment, setAutoPrintReceiptOnPayment] = useState(false);
  // Terminal tips enabled — drives the unpaid customer bill's Tip:/Total: write-in area.
  const [receiptTipsEnabledSetting, setReceiptTipsEnabledSetting] = useState(false);
  // Clover terminal tip methods (clover-terminal-charge). When tips are on, each enabled
  // method becomes its OWN charge button on a Clover ticket, mirroring the Stripe reader
  // flow: on-reader = the Clover device asks the customer; on-bill = staff enter a written
  // tip on the iPad before charging. Both off => a single plain "Charge on Clover terminal".
  const [cloverTipOnReaderSetting, setCloverTipOnReaderSetting] = useState(false);
  const [cloverTipOnBillSetting, setCloverTipOnBillSetting] = useState(false);
  // Square Terminal device-tip setting (square-terminal-checkout). Independent of the
  // Stripe-reader tip flag above; drives allow_tipping when charging on a Square Terminal.
  const [squareTerminalTipsEnabledSetting, setSquareTerminalTipsEnabledSetting] = useState(false);
  // "Square App" (Take Payment) button visibility. Default off — hidden unless the tenant
  // opts in (most collect on the Square Terminal directly, making the Square-app hop redundant).
  const [squareAppButtonEnabledSetting, setSquareAppButtonEnabledSetting] = useState(false);
  // POS print paper size. One of the values in utils/paperSize.js. Replaces the old
  // orderDetailsOnly boolean, which conflated content-type with paper size. Content-type
  // is now chosen per action (Print Ticket vs Print Receipt vs auto-print), not here.
  const [printPaperSize, setPrintPaperSize] = useState(DEFAULT_PAPER_SIZE);
  const [printBrand, setPrintBrand] = useState({ businessName: '', logoUrl: '' });
  const [slaConfig, setSlaConfig] = useState({ enabled: false, greenMinutes: 5, orangeMinutes: 10, redMinutes: 15 });
  const [slaNowMs, setSlaNowMs] = useState(() => Date.now());
  // Ticking clock that drives the scheduled hold->release transition. Without a clock in
  // the filter's dependency path, a held order would only cross into ACTIVE on a refetch or
  // manual Refresh; this advances "now" every 30s so release happens on time on its own.
  const [scheduleNowMs, setScheduleNowMs] = useState(() => Date.now());

  // Table party subdivision gate (table-party-subdivision R1.10, R1.11, R1.12).
  //
  // INITIALIZED FALSE, AND READ FROM THE QUEUE RESPONSE, NOT `/admin/general`.
  //
  // Two things would go wrong the obvious way. First, `loadPrintingSettings()` below
  // fetches `/admin/general`, which requires `General Settings: View`; a POS-only
  // cashier does not hold it, that call returns early on the 403, and the gate would
  // resolve FALSE for exactly the user the disambiguation protects. `fetchQueue` already
  // carries `posSla` for precisely this reason, and the backend now carries the party
  // gate the same way. Second, a gate initialized `true` would flash the banner and the
  // guest counts at every non-opted-in tenant on the render before settings resolve.
  //
  // Held across pagination rather than recomputed per page: `fetchQueue` pages, and only
  // whichever page carried the key should decide, or a later page without it would flip
  // the gate off mid-list.
  const [partiesEnabled, setPartiesEnabled] = useState(false);

  // In-app card payment gate (in-app-card-payment spec). READ FROM THE QUEUE RESPONSE,
  // NOT `/admin/general`, and INITIALIZED FALSE, for the exact reasons `partiesEnabled`
  // is: `/admin/general` requires "General Settings: View", which a POS-only cashier does
  // not hold, so reading the gate there would resolve it false for the very user who runs
  // the queue. Held across pagination the same way — only a page that actually carries the
  // key may flip it, or a later page without it would turn the button off mid-list.
  const [inAppCardEnabled, setInAppCardEnabled] = useState(false);
  // Ref mirror of the flag, updated synchronously in fetchPage. The gate ORs this with
  // the state so the "Charge card" button resolves correctly even on the render pass
  // where the state setter has not yet flushed (the queue can load via a paginated /
  // background path, and a per-render read of just-set state can lag by one commit).
  const inAppCardEnabledRef = useRef(false);
  // Tenant POS provider from the queue response, for the in-app card gate. An IN-STORE /
  // staff ticket carries no `posProvider` of its own, so the gate falls back to this.
  const [posTenantProvider, setPosTenantProvider] = useState('');
  const posTenantProviderRef = useRef('');

  // Scheduled-orders release lead (minutes) from the queue response (scheduled-orders
  // spec R6). MUST be state, not a ref: the hold/release decision lives in the
  // `filteredOrders` useMemo, and a ref update does not retrigger that memo — so a ref
  // would leave the filter computing with the initial 0, which makes release = the slot
  // time itself and holds every scheduled order until its exact scheduled minute instead
  // of posReleaseMinutes before it. (This was the "8:30 order never falls into ACTIVE"
  // bug.) As state it is a memo dependency, so the correct lead is applied as soon as the
  // queue response carries it. The ref mirror is kept for any non-reactive reader.
  const [scheduledReleaseMinutes, setScheduledReleaseMinutes] = useState(0);
  const scheduledReleaseMinutesRef = useRef(0);
  // Active Stripe "Charge card" session for one order, or null. Drives the payment modal.
  const [chargeCardOrder, setChargeCardOrder] = useState(null);

  // Razorpay UPI at-store QR (razorpay-upi-dinein-qr spec). Mirrors the in-app-card flag +
  // modal state above. The enable flag rides the queue response (same permission reason:
  // a POS-only cashier's /admin/general 403s) and is ORed with a ref mirror to avoid a
  // one-commit lag during paginated/background loads. `upiQrOrder` drives the QR modal.
  const [razorpayUpiEnabled, setRazorpayUpiEnabled] = useState(false);
  const razorpayUpiEnabledRef = useRef(false);
  const [upiQrOrder, setUpiQrOrder] = useState(null);

  // ---------------------------------------------------------------------------
  // Square Terminal (card-present) — square-terminal-checkout
  //
  // Square does NOT surface an unpaid API-created order on the Terminal's own
  // screen, so a cashier cannot ring up a PAY_AT_POS ticket from the device. The
  // Terminal API is the sanctioned path: push a checkout to a paired device.
  //
  // `squareTerminalPaired` is read from /admin/pos/terminal/device-status, which is
  // authorized under "POS Queue: View" precisely so a POS-only cashier can resolve
  // it (their /admin/general and admin-only POS status reads 403). It returns 200
  // with paired:false when nothing is paired, so a tenant with no Terminal simply
  // never sees the button — no error banner.
  const [squareTerminalPaired, setSquareTerminalPaired] = useState(false);
  // The in-flight pushed checkout: { key, orderNumber, checkoutId, status } or null.
  // Only one at a time — a Terminal can only prompt for one card.
  const [terminalCharge, setTerminalCharge] = useState(null);
  const terminalPollRef = useRef(null);

  // Poll handle for the in-flight Clover charge (mirrors terminalPollRef for Square). The
  // Clover charge is now ASYNC: the checkout POST returns 202 immediately and the card
  // outcome is observed by polling the status endpoint, because the card-present tap+sign
  // window exceeds API Gateway's ~29s timeout.
  const cloverTerminalPollRef = useRef(null);

  // CLOVER Terminal Charge (clover-terminal-charge). Mirrors the Square terminal state
  // above but for a Clover device. `cloverTerminalPaired` comes from
  // /admin/pos/clover/terminal/status (POS Queue: View, silent 200 when unpaired, so a
  // non-Clover tenant simply never sees the button). Like Square, the Clover charge is now
  // ASYNC: the checkout POST returns 202 {status:'PENDING'} immediately and the outcome is
  // observed by polling /admin/pos/clover/terminal/checkout/status, because holding the
  // card-present tap+sign window open synchronously exceeds API Gateway's ~29s timeout.
  // The Clover payment webhook is the settlement backstop; this poll only reflects state.
  // `cloverTerminalCharge` = { key, orderNumber, orderTimestamp } while a charge is
  // mid-flight (used to show "waiting on device" + a Cancel button), else null.
  const [cloverTerminalPaired, setCloverTerminalPaired] = useState(false);
  const [cloverTerminalCharge, setCloverTerminalCharge] = useState(null);

  // SHIFT4 Terminal Charge (shift4-payment-integration). Mirrors the Clover/Square terminal
  // state but for a Shift4 terminal. `shift4TerminalPaired` comes from
  // /admin/pos/connect/shift4/status (silent 200 when not connected/paired, so a non-Shift4
  // tenant simply never sees the button). Unlike Clover, the Shift4 Sale is SYNCHRONOUS, so
  // there is no PENDING poll — the charge POST returns approved/declined directly. `shift4Busy`
  // guards against a double-click while one charge is in flight.
  const [shift4TerminalPaired, setShift4TerminalPaired] = useState(false);
  const [shift4Busy, setShift4Busy] = useState(false);

  const prevQueueKeysRef = useRef(new Set());
  const hasLoadedQueueOnceRef = useRef(false);
  const autoPrintedKeysRef = useRef(new Set());
  // Newest order_timestamp (epoch seconds) observed so far. Used to tell a genuine
  // new arrival from an old order that only becomes visible after a filter change.
  const maxSeenOrderTsRef = useRef(0);

  const loadPrintingSettings = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/general`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      // Relabel all money in the queue with the tenant's currency (e.g. INR -> ₹).
      // Self-sufficient here so the queue never renders stale USD if App.js's
      // currency fetch hasn't resolved yet. Relabels only; never converts values.
      setStoreCurrency(
        data?.settings?.currency ?? data?.currency,
        data?.settings?.locale ?? data?.locale,
      );
      // Display timezone for the "today" filter and for printed times. Secondary source:
      // the queue response is what a POS-only cashier gets, and both derive from the same
      // stored settings item so they cannot disagree. Absent/unrecognised => Eastern.
      setTenantTimezone(data);
      const autoPrintEnabled = Boolean(data?.settings?.posPrinting?.autoPrintNewOrders);
      console.log(`[AUTO-PRINT] Feature enabled: ${autoPrintEnabled}`);
      setAutoPrintNewOrdersEnabled(autoPrintEnabled);
      setAutoPrintReceiptOnPayment(Boolean(data?.settings?.posPrinting?.autoPrintReceiptOnPayment));
      setReceiptTipsEnabledSetting(data?.settings?.terminalTipsEnabled === true);
      // Clover tip-method flags: only meaningful when tips are enabled. Each drives one
      // charge button on a Clover ticket.
      {
        const tipsOn = data?.settings?.terminalTipsEnabled === true;
        setCloverTipOnReaderSetting(tipsOn && data?.settings?.terminalTipOnReader === true);
        setCloverTipOnBillSetting(tipsOn && data?.settings?.terminalTipOnBill === true);
      }
      // Square Terminal device tips (square-terminal-checkout) — a SEPARATE flag from the
      // Stripe-reader terminalTipsEnabled above. Drives allow_tipping on the "Charge on
      // Square Terminal" action so the device shows a tip screen.
      setSquareTerminalTipsEnabledSetting(data?.settings?.squareTerminalTipsEnabled === true);
      // "Square App" (Take Payment) button visibility. Default off (absent => hidden).
      setSquareAppButtonEnabledSetting(data?.settings?.squareAppButtonEnabled === true);
      // Migrate the legacy boolean: a record from before this change has no paperSize
      // and its old orderDetailsOnly toggle only ever produced half-letter, which is what
      // the shared normalizer defaults to.
      setPrintPaperSize(normalizePaperSize(data?.settings?.posPrinting?.paperSize));
      const sla = data?.settings?.posSla;
      if (sla && typeof sla === 'object') {
        setSlaConfig({
          enabled: Boolean(sla.enabled),
          greenMinutes: Number(sla.greenMinutes),
          orangeMinutes: Number(sla.orangeMinutes),
          redMinutes: Number(sla.redMinutes),
        });
      }
    } catch {
    }
  };

  const loadLocations = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/locations`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const list = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(list);
    } catch (e) {
      console.error('Failed to load locations:', e);
    }
  };

  // Re-tick "now" every 30s so SLA bands advance (green→orange→red) for orders
  // sitting in OPEN state, without needing a queue refetch. Only runs when the
  // SLA feature is enabled.
  useEffect(() => {
    if (!slaConfig?.enabled) return undefined;
    const id = window.setInterval(() => setSlaNowMs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [slaConfig?.enabled]);

  // Advance the scheduled-release clock every 30s so a held order moves into ACTIVE at its
  // release instant without waiting for a refetch. Runs only when scheduling is in play.
  useEffect(() => {
    if (!scheduledReleaseMinutes && scheduledReleaseMinutes !== 0) return undefined;
    const id = window.setInterval(() => setScheduleNowMs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [scheduledReleaseMinutes]);

  const loadPrintBranding = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/theme`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const theme = (data?.theme && typeof data.theme === 'object') ? data.theme : data;
      const nextBusinessName = String(theme?.footerBusinessName || '').trim();
      const nextLogoUrl = String(theme?.heroLogoDarkUrl || theme?.heroLogoUrl || theme?.heroLogoLightUrl || '').trim();
      setPrintBrand({ businessName: nextBusinessName, logoUrl: nextLogoUrl });
    } catch {
    }
  };

  // Record that a GUEST BILL was printed, so a later addition can be detected as having
  // invalidated it (order-additions R9.1). Fire-and-forget: a print must never fail
  // because the stamp did not persist, and the stamp is a freshness hint rather than
  // anything money depends on.
  //
  // Only called for the FULL receipt. The `orderDetailsOnly` variant carries no totals,
  // so it is a kitchen document and printing it does not create a bill to go stale.
  const recordBillPrinted = async (order) => {
    try {
      const orderNumber = String(order?.order_number || '').trim();
      const orderTimestamp = Number(order?.order_timestamp);
      if (!orderNumber || !Number.isFinite(orderTimestamp) || orderTimestamp <= 0) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      await fetch(`${API_BASE_URL}/admin/pos/orders/bill-printed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
      });
    } catch {
      // Non-fatal by design.
    }
  };

  // contentType: 'ticket' (kitchen, no prices) | 'receipt' (itemized bill).
  // Paper size comes from the tenant setting and applies to both. `silent` marks the
  // auto-print path (timer, no user gesture, cannot open a window).
  const onPrintOrder = async (order, { silent = false, contentType = 'receipt' } = {}) => {
    try {
      const resolvedContentType = contentType === 'ticket' ? 'ticket' : 'receipt';
      const isReceipt = resolvedContentType === 'receipt';
      const html = buildPosOrderReceiptHtml(order, {
        businessName: printBrand?.businessName,
        logoUrl: printBrand?.logoUrl,
        address: printBrand?.address,
        phone: printBrand?.phone,
        contentType: resolvedContentType,
        // Customer receipt is 80mm-thermal-native; the kitchen ticket keeps the tenant paper.
        paperSize: isReceipt ? 'thermal_80mm' : printPaperSize,
        partiesEnabled,
        tipsEnabled: receiptTipsEnabledSetting,
      });
      printReceiptHtml(html, { allowNewWindow: !silent });
      // Only the priced RECEIPT creates a bill that a later addition could invalidate.
      // The kitchen ticket and auto-print (also a ticket) carry no totals, so they
      // never stamp bill-printed.
      if (resolvedContentType === 'receipt') recordBillPrinted(order);
    } catch (e) {
      if (silent) return;
      try {
        window.alert(e?.message || 'Unable to print');
      } catch {
      }
    }
  };

  // Auto-print the customer RECEIPT when an order is paid at the counter (settle-cash /
  // charge-card), IF the tenant enabled autoPrintReceiptOnPayment. Silent + best-effort.
  // The order is printed as paid so the receipt shows the recorded total (+ tip if any).
  // Dine-in bills are printed on demand BEFORE payment and never reach here, so this only
  // fires the pay-at-store / counter case, as intended.
  const maybeAutoPrintReceiptOnPayment = (order) => {
    try {
      console.log(`[AUTO-RECEIPT] triggered; setting enabled=${autoPrintReceiptOnPayment}, hasOrder=${Boolean(order)}`);
      if (!autoPrintReceiptOnPayment) {
        console.log('[AUTO-RECEIPT] skipped: autoPrintReceiptOnPayment is OFF');
        return;
      }
      if (!order) {
        console.log('[AUTO-RECEIPT] skipped: no order');
        return;
      }
      const paidOrder = { ...order, payment_status: 'paid' };
      console.log(`[AUTO-RECEIPT] printing receipt for order ${order?.order_number}`);
      onPrintOrder(paidOrder, { silent: true, contentType: 'receipt' });
    } catch (e) {
      // Best-effort: never fail the settle because a receipt did not print.
      console.log(`[AUTO-RECEIPT] error: ${e?.message || e}`);
    }
  };

  // Print the chit for ONE addition batch. Defaults to the most recent, which is what a
  // server wants immediately after adding; earlier batches remain reachable by index.
  const onPrintAdditionChit = (order, index = -1) => {
    try {
      const log = Array.isArray(order?.amendments) ? order.amendments : [];
      if (!log.length) return;
      const amendment = index >= 0 && index < log.length ? log[index] : log[log.length - 1];
      // Always tap-driven, so opening a window is permitted.
      printReceiptHtml(
        buildAdditionChitHtml(order, amendment, { businessName: printBrand?.businessName }),
        { allowNewWindow: true }
      );
    } catch (e) {
      try {
        window.alert(e?.message || 'Unable to print');
      } catch {
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!API_BASE_URL) return;
        const res = await fetch(`${API_BASE_URL}/public/general`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const nextEnabled = Boolean(data?.posTaxEnabled);
        const nextModeRaw = String(data?.posTaxCollectionMode || 'PROVIDER').trim().toUpperCase();
        const nextMode = nextModeRaw === 'OWN' ? 'OWN' : 'PROVIDER';
        const rateRaw = data?.posTaxRatePercent;
        const nextRate = rateRaw === null || rateRaw === undefined ? null : Number(rateRaw);

        if (!cancelled) {
          setPosTaxEnabled(nextEnabled);
          setPosTaxCollectionMode(nextMode);
          setPosTaxRatePercent(Number.isFinite(nextRate) ? nextRate : null);
        }
      } catch {
        // ignore
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOrders = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    const wantStatus = String(orderStatusFilter || 'all');
    const strictToday = dateMode === 'today' || (dateMode === 'preset' && String(presetKey || '').trim().toLowerCase() === '1d');

    return (orders || []).filter((o) => {
      if (strictToday && !isTodayInZone(o?.order_timestamp)) return false;

      const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
      const paymentStatus = String(o?.payment_status || o?.paymentStatus || '').trim().toLowerCase();
      const rawFulfillment = String(o?.adminFulfillmentStatus || o?.squareFulfillmentState || '').trim();
      const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
      const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
      // An order with no later fulfillment status is treated as OPEN regardless
      // of payment method (online-paid, pay-at-store) or fulfillment type, so the
      // OPEN filter and SLA stay consistent across all orders.
      const effectiveFulfillment = fulfillmentLabel || 'OPEN';
      const isFulfillmentCancelled = String(fulfillmentLabel || '').trim().toUpperCase() === 'CANCELLED';
      const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED' || isFulfillmentCancelled;

      // Status filtering lives in a shared, unit-tested helper (see queueStatusFilter.js).
      // The inline version here treated `active` as "not cancelled" only, which is why
      // delivered / picked-up / completed orders stayed in the ACTIVE view.
      if (
        !matchesOrderStatusFilter({
          selection: wantStatus,
          fulfillmentLabel: effectiveFulfillment,
          isCancelled,
          // Scheduled-orders release timing (spec R6). Present only on orders a customer
          // scheduled; `posReleaseMinutes` from the queue response. Absent either, the
          // helper treats the order as normal/ASAP — today's behavior unchanged.
          isScheduled: o?.isScheduled === true,
          scheduledTime: o?.scheduledTime,
          posReleaseMinutes: scheduledReleaseMinutes || 0,
          now: Math.floor(scheduleNowMs / 1000),
        })
      ) {
        return false;
      }

      if (!term) return true;
      const orderNumber = String(o?.order_number || '').toLowerCase();
      const name = String(o?.customer_name || '').toLowerCase();
      const email = String(o?.customer_email || '').toLowerCase();
      const phone = String(o?.customer_phone || '').toLowerCase();
      if (orderNumber.includes(term) || name.includes(term) || email.includes(term) || phone.includes(term)) {
        return true;
      }
      // Table and party terms (table-party-subdivision R6.3, R6.4), GATED.
      //
      // Widening a search reads like a pure improvement, which is exactly why it is easy
      // to ship unconditionally by mistake. It is not neutral: it changes which orders a
      // query returns for a tenant that never opted in, and R6.8 pins that tenant to
      // today's four fields exactly. So when the gate is off this returns false here and
      // the predicate above is the whole predicate, bit for bit.
      if (!partiesEnabled) return false;
      const table = String(o?.tableLabel || o?.customerSubmittedSnapshot?.tableLabel || '').toLowerCase();
      const party = partyLabelOf(o).toLowerCase();
      const partyName = partyNameOf(o).toLowerCase();
      return table.includes(term) || party.includes(term) || partyName.includes(term);
    });
  }, [orders, searchTerm, orderStatusFilter, dateMode, presetKey, partiesEnabled, scheduledReleaseMinutes, scheduleNowMs]);

  // How many OPEN tickets each table holds (table-party-subdivision R6.5, R6.6, R8.2).
  //
  // Computed once and shared by the adjacency sort, the multi-ticket banner and the
  // settle confirmation, so the three cannot disagree about whether a table is
  // ambiguous. An empty Map when the gate is off, which is what makes every consumer
  // inert without each needing its own gate check.
  const tableTicketCounts = useMemo(
    () => (partiesEnabled ? openTicketCountsByTable(filteredOrders) : new Map()),
    [partiesEnabled, filteredOrders]
  );

  // Group a table's open tickets together (R6.5).
  //
  // The primary defence: two tickets for table 12 separated by six unrelated orders is
  // the state in which a cashier settles the wrong one, because nothing on screen says
  // there are two. Sorting them adjacent converts an invisible ambiguity into a visible
  // one.
  //
  // GATED, and returns `filteredOrders` UNCHANGED when off — not a copy, not a stable
  // re-sort. Two dine-in tickets on one table is a state `customer1` reaches today, so
  // an ungated sort would silently reorder a demo queue.
  //
  // Within a shared table, ticket order follows the party label so A reads above B.
  // Everything else keeps the timestamp order the queue arrived in: `Array.prototype.sort`
  // is stable in every supported engine, so returning 0 preserves it.
  const displayOrders = useMemo(() => {
    if (!partiesEnabled) return filteredOrders;
    if (tableTicketCounts.size === 0) return filteredOrders;
    const sharedTableOf = (o) => {
      const key = normalizeTableLabel(o?.tableLabel || o?.customerSubmittedSnapshot?.tableLabel);
      if (!key) return '';
      return (tableTicketCounts.get(key) || 0) >= 2 ? key : '';
    };
    const anchor = new Map();
    filteredOrders.forEach((o, index) => {
      const key = sharedTableOf(o);
      // The FIRST position at which a shared table appears becomes the whole group's
      // sort position. Grouping without an anchor would hoist shared tables to the top
      // of the queue and break the newest-first reading the rest of the screen relies on.
      if (key && !anchor.has(key)) anchor.set(key, index);
    });
    if (anchor.size === 0) return filteredOrders;
    return filteredOrders
      .map((o, index) => ({ o, index, key: sharedTableOf(o) }))
      .sort((a, b) => {
        const aPos = a.key ? anchor.get(a.key) : a.index;
        const bPos = b.key ? anchor.get(b.key) : b.index;
        if (aPos !== bPos) return aPos - bPos;
        if (a.key && a.key === b.key) {
          const cmp = partyLabelOf(a.o).localeCompare(partyLabelOf(b.o));
          if (cmp !== 0) return cmp;
        }
        return a.index - b.index;
      })
      .map((entry) => entry.o);
  }, [partiesEnabled, filteredOrders, tableTicketCounts]);

  const maskPhone = (raw) => {
    const s = String(raw || '').trim();
    const digits = s.replace(/\D/g, '');
    if (digits.length >= 4) return `***${digits.slice(-4)}`;
    return s;
  };

  // Settlement safeguard (table-party-subdivision R8.1, R8.2, R8.3, R8.4, R8.5).
  //
  // `POST /admin/pos/orders/settle-cash` has NO staff-facing undo (an undo feature was
  // considered and rejected), so this dialog is the last
  // point at which settling the wrong group's bill is recoverable. It therefore states
  // the three things a cashier can check against the paper in their hand: the table, the
  // party, and the amount.
  //
  // AMBIGUOUS MEANS: this order carries a party, OR its table holds two or more open
  // tickets. The second half matters on its own — an unlabelled ticket sharing a table
  // is exactly as easy to mis-settle as a labelled one.
  //
  // Returns TRUE (proceed) when the gate is off or nothing is ambiguous, so the caller
  // falls through to its existing confirm and a non-opted-in tenant sees no new dialog
  // at any ticket count (R8.4). An unexpected dialog mid-demo is a visible behaviour
  // change, not a harmless extra click.
  const confirmPartySettlement = (order) => {
    if (!partiesEnabled) return true;
    const party = partyLabelOf(order);
    const shared = hasSharedTable(order, tableTicketCounts);
    if (!party && !shared) return true;

    const orderNumber = String(order?.order_number || '').trim();
    const table = String(order?.tableLabel || order?.customerSubmittedSnapshot?.tableLabel || '').trim();
    const name = partyNameOf(order);
    const total = formatMoney(order?.posFinalTotalMoney, order?.amount_total_cents);
    const count = tableTicketCounts.get(
      normalizeTableLabel(order?.tableLabel || order?.customerSubmittedSnapshot?.tableLabel),
    ) || 0;

    const lines = [
      shared && count >= 2
        ? `Table ${table} has ${count} open tickets. Settle the RIGHT one.`
        : 'Confirm the party before settling.',
      '',
      `Table:  ${table || '—'}`,
      `Party:  ${party || '(none)'}${name ? ` · ${name}` : ''}`,
      `Total:  ${total}`,
      `Order:  ${orderNumber}`,
      '',
      'Does this match the bill the guest handed you?',
    ];
    return window.confirm(lines.join('\n'));
  };

  const markAsPaid = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    // Party confirmation FIRST, then the pre-existing one. Declining either sends
    // nothing (R8.3). Two dialogs only ever appear for an ambiguous ticket on an
    // opted-in tenant; every other order is exactly one confirm, as today.
    if (!confirmPartySettlement(order)) return;

    const ok = window.confirm(`Mark order ${orderNumber} as PAID?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Marking paid…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // POS-scoped settlement route, NOT /admin/orders/mark-paid. Two reasons:
      //   * mark-paid is gated on ("Orders/Reports","Edit"), so a cashier holding only
      //     `POS Queue` got a 403 on the one action their job requires.
      //   * settle-cash does not write status="Processing", so a settled dine-in ticket
      //     stays out of the ecommerce shipping-label backlog.
      // The online-orders queue (AdminOrdersQueue.js) keeps using mark-paid.
      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/settle-cash`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark paid'));
      setStatusMessage(`Order ${orderNumber} marked PAID.`);
      // Auto-print the customer receipt on payment (pay-at-store / counter) if enabled.
      // Best-effort and silent; the order is printed as paid.
      maybeAutoPrintReceiptOnPayment(order);
    } catch (e) {
      setError(e?.message || 'Failed to mark paid');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  // Combined "Paid and <fulfillment> complete" for dine-in and pickup POS orders.
  //
  // Two sequential calls (option A, no combined backend route): settle-cash to mark
  // the ticket PAID, then fulfillment/update to set the terminal fulfillment status.
  // Order matters — payment first — so that if the second call fails the ticket is at
  // least settled and the operator can complete fulfillment with the still-visible
  // chips. A partial failure is surfaced explicitly rather than swallowed.
  //
  // Reuses the same settle-cash route as markAsPaid (cashier POS-Queue permission,
  // no status="Processing" stamp) and the same fulfillment/update route as the chips,
  // so no new authz. Only offered for dine-in / pickup; delivery is untouched because
  // an online delivery order arrives paid and has no pay-at-restaurant step.
  const markPaidAndComplete = async (order, fulfillmentTarget, actionLabel) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    const orderTimestamp = order?.order_timestamp;
    if (!orderNumber || !orderTimestamp) {
      setError('Missing order identifiers.');
      return;
    }
    const target = String(fulfillmentTarget || '').trim();
    if (!target) return;

    // Party confirmation FIRST (same as markAsPaid), then the combined confirm.
    if (!confirmPartySettlement(order)) return;

    const ok = window.confirm(`Mark order ${orderNumber} as PAID and ${actionLabel}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Marking paid…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payRes = await fetch(`${API_BASE_URL}/admin/pos/orders/settle-cash`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });
      const payData = await payRes.json().catch(() => ({}));
      if (!payRes.ok) throw new Error(friendlyApiError(payRes.status, payData, 'Failed to mark paid'));

      // Payment succeeded; now the fulfillment half. A failure here leaves a PAID
      // ticket, which is why the message distinguishes the two outcomes.
      setStatusMessage('Marked paid. Updating fulfillment…');
      const fulRes = await fetch(`${API_BASE_URL}/admin/pos/orders/fulfillment/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: orderTimestamp,
          adminFulfillmentStatus: target,
        }),
      });
      const fulData = await fulRes.json().catch(() => ({}));
      if (!fulRes.ok) {
        throw new Error(
          friendlyApiError(fulRes.status, fulData, `Order ${orderNumber} was marked PAID, but the fulfillment update failed. Use the fulfillment buttons to complete it.`)
        );
      }

      setStatusMessage(`Order ${orderNumber}: PAID and ${actionLabel}.`);
      // Auto-print the customer receipt on payment if enabled (this action IS the payment
      // moment for a dine-in / pickup ticket). Best-effort and silent.
      maybeAutoPrintReceiptOnPayment(order);
    } catch (e) {
      setError(e?.message || 'Failed to complete order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const updateFulfillment = async (order, nextStatus) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    const orderTimestamp = order?.order_timestamp;
    if (!orderNumber || !orderTimestamp) {
      setError('Missing order identifiers.');
      return;
    }

    const target = String(nextStatus || '').trim();
    if (!target) return;

    const ok = window.confirm(`Set fulfillment for order ${orderNumber} to ${formatFulfillmentStatus(target) || target}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Updating fulfillment status…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/fulfillment/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: orderTimestamp,
          adminFulfillmentStatus: target,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to update fulfillment status'));

      const warn = data?.squareSyncAttempted && !data?.squareSyncOk;
      setStatusMessage(warn ? 'Updated. Square not updated.' : 'Updated.');
      if (warn && data?.squareSyncError) {
        window.alert(`Square sync warning: ${String(data.squareSyncError)}`);
      }
    } catch (e) {
      setError(e?.message || 'Failed to update fulfillment status');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const callbackUrl = useMemo(() => {
    try {
      if (!API_BASE_URL) return '';
      return `${API_BASE_URL}/admin/pos/take-payment/callback`;
    } catch {
      return '';
    }
  }, []);

  const cancelOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    const orderTimestamp = order?.order_timestamp;
    if (!orderNumber || !orderTimestamp) {
      setError('Missing order identifiers.');
      return;
    }

    const posProvider = String(order?.posProvider || '').trim().toUpperCase();
    const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : posProvider === 'PHONEPE' ? 'PhonePe' : posProvider === 'RAZORPAY' ? 'Razorpay' : '';
    const paymentLinkUrl = String(order?.posPaymentLink?.href || order?.posPaymentLink?.long_url || order?.posPaymentLink?.url || '').trim();
    const shouldAutoRefund = (posProvider === 'SQUARE' || posProvider === 'CLOVER') && (String(order?.posPaymentMode || '').trim().toUpperCase() === 'PAY_NOW' || !!paymentLinkUrl);
    const refundLine = shouldAutoRefund
      ? posProvider === 'SQUARE'
        ? 'If this was paid online, this will attempt an automatic Square refund.'
        : 'If this was paid online, this will attempt an automatic Clover void/refund.'
      : posProvider === 'SQUARE'
        ? 'Refund will not be automatic. If this was paid in-person, refund in Square POS.'
        : posProvider === 'CLOVER'
          ? 'Refund will not be automatic. Refund/void must be handled manually in Clover.'
          : providerLabel
            ? `Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`
            : 'No external POS provider is associated with this order.';

    const ok = window.confirm(`Cancel order ${orderNumber}?\n\nThis will cancel the order in EzSell.\n\n${refundLine}`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Cancelling order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: orderTimestamp,
          reason: '',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel order'));

      const refundMsg = data?.squareRefundId
        ? `Refund: ${String(data.squareRefundStatus || 'submitted')} (${String(data.squareRefundId)})`
        : data?.stripeRefundOk
          ? `Refund: succeeded (Stripe${data.stripeRefundId ? ' ' + String(data.stripeRefundId) : ''})`
          : data?.stripeRefundAttempted && !data?.stripeRefundOk
            ? `Refund: failed (${String(data.stripeRefundError || 'unknown')})`
        : data?.cloverRefundAttempted
          ? data?.cloverRefundOk
            ? `Refund: ${String(data.cloverRefundAction || 'submitted')} (Clover)`
            : `Refund: Clover failed (${String(data.cloverRefundError || 'unknown')})`
          : data?.cloverRefundSkipReason
            ? `Refund: skipped (${String(data.cloverRefundSkipReason)})`
          : shouldAutoRefund
            ? 'Refund: not issued (no completed online payment found)'
            : posProvider === 'SQUARE'
              ? 'Refund: manual in Square POS'
              : posProvider === 'CLOVER'
                ? 'Refund: manual in Clover'
                : providerLabel
                  ? `Refund: manual in ${providerLabel}`
                  : 'Refund: not applicable (no external POS provider)';

      const providerMsg =
        posProvider === 'SQUARE'
          ? data?.squareCancelOk
            ? 'Square: cancelled'
            : data?.squareCancelWarning
              ? `Square warning: ${String(data.squareCancelWarning)}`
              : 'Square: cancel not confirmed'
          : posProvider === 'CLOVER'
            ? data?.cloverCancelAttempted
              ? data?.cloverCancelOk
                ? 'Clover: cancelled'
                : `Clover: cancel failed (${String(data.cloverCancelError || 'unknown')})`
              : data?.cloverRefundAttempted
                ? data?.cloverRefundOk
                  ? `Clover: ${String(data.cloverRefundAction || 'refund')} submitted`
                  : `Clover: refund failed (${String(data.cloverRefundError || 'unknown')})`
                : data?.cloverRefundSkipReason
                  ? `Clover: refund skipped (${String(data.cloverRefundSkipReason)})`
                  : 'Clover: no POS action attempted'
            : posProvider === 'STRIPE'
              ? data?.stripeRefundOk
                ? 'Stripe: refund issued'
                : data?.stripeRefundAttempted
                  ? `Stripe: refund failed (${String(data.stripeRefundError || 'unknown')})`
                  : 'Stripe: order cancelled'
              : providerLabel
                ? `${providerLabel}: cancel in POS not attempted`
                : 'No external POS action attempted';

      window.alert(`Cancelled order ${orderNumber}.\n\n${providerMsg}\n${refundMsg}`);

      setStatusMessage(`Order ${orderNumber} cancelled.`);

      try {
        setOrders((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list
            .map((it) => {
              const itNum = String(it?.order_number || '').trim();
              const itTs = it?.order_timestamp;
              if (itNum === orderNumber && String(itTs ?? '') === String(orderTimestamp ?? '')) {
                return { ...it, status: 'CANCELLED', order_status: 'CANCELLED' };
              }
              return it;
            })
            .filter((it) => {
              const want = String(orderStatusFilter || 'all').trim().toUpperCase();
              if (want === 'ALL' || want === 'CANCELLED') return true;
              const itNum = String(it?.order_number || '').trim();
              const itTs = it?.order_timestamp;
              if (itNum === orderNumber && String(itTs ?? '') === String(orderTimestamp ?? '')) return false;
              return true;
            });
          return next;
        });
      } catch {
        // ignore
      }
    } catch (e) {
      setError(e?.message || 'Failed to cancel order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  // Uber Eats accept/reject (Requirements 12.5, 7.1-7.4). Strictly additive:
  // these only run for Uber Eats orders and call the marketplace-aware
  // admin-orders-handler routes. The backend confirms order_source=ubereats
  // and routes to the Uber Eats accept/deny endpoints.
  const acceptUberEatsOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const ok = window.confirm(`Accept Uber Eats order ${orderNumber}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Accepting Uber Eats order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to accept Uber Eats order'));

      setStatusMessage(`Uber Eats order ${orderNumber} accepted.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to accept Uber Eats order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const rejectUberEatsOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const reason = window.prompt(
      `Reject Uber Eats order ${orderNumber}?\n\nEnter a reason (sent to Uber Eats):`,
      'Store unable to fulfill'
    );
    // window.prompt returns null when cancelled; empty string is allowed but we
    // require a non-empty reason for the deny call.
    if (reason === null) return;
    const trimmedReason = String(reason || '').trim();
    if (!trimmedReason) {
      setError('A reason is required to reject an Uber Eats order.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Rejecting Uber Eats order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: trimmedReason }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to reject Uber Eats order'));

      setStatusMessage(`Uber Eats order ${orderNumber} rejected.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to reject Uber Eats order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  // DoorDash accept/reject (Requirements 15.6, 7.1-7.4). Strictly additive and
  // parallel to the Uber Eats handlers above: these only run for DoorDash
  // orders and call the same marketplace-aware admin-orders-handler routes. The
  // backend confirms order_source=doordash and routes to the DoorDash
  // confirm/reject endpoints.
  const acceptDoorDashOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const ok = window.confirm(`Accept DoorDash order ${orderNumber}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Accepting DoorDash order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to accept DoorDash order'));

      setStatusMessage(`DoorDash order ${orderNumber} accepted.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to accept DoorDash order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const rejectDoorDashOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const reason = window.prompt(
      `Reject DoorDash order ${orderNumber}?\n\nEnter a reason (sent to DoorDash):`,
      'Store unable to fulfill'
    );
    // window.prompt returns null when cancelled; empty string is allowed but we
    // require a non-empty reason for the reject call.
    if (reason === null) return;
    const trimmedReason = String(reason || '').trim();
    if (!trimmedReason) {
      setError('A reason is required to reject a DoorDash order.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Rejecting DoorDash order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: trimmedReason }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to reject DoorDash order'));

      setStatusMessage(`DoorDash order ${orderNumber} rejected.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to reject DoorDash order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const takePayment = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (!callbackUrl) {
      setError('Missing callback URL.');
      return;
    }
    if (busy) return;

    // Same safeguard as "Mark as Paid" (R8.1, R8.2). This path hands the guest to a
    // Square terminal for a specific amount, so settling the wrong ticket here charges
    // the wrong group — the reason the Settle_Action covers both actions and not just
    // the one with an obvious confirm already on it.
    if (!confirmPartySettlement(order)) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const orderNumber = String(order?.order_number || '').trim();
      const name = String(order?.customer_name || '').trim();
      const phoneDigits = String(order?.customer_phone || '').replace(/\D/g, '');
      const phoneLast4 = phoneDigits.length >= 4 ? phoneDigits.slice(-4) : '';
      const noteParts = [`Order ${orderNumber}`];
      if (name) noteParts.push(name);
      if (phoneLast4) noteParts.push(`***${phoneLast4}`);
      const paymentNotes = noteParts.join(' • ');

      const res = await fetch(`${API_BASE_URL}/admin/pos/take-payment/link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: order?.order_timestamp,
          callback_url: callbackUrl,
          ui_redirect: `${window.location.origin}/admin/channels/pos/queue`,
          supported_tender_types: ['CREDIT_CARD', 'CASH'],
          notes: paymentNotes,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to generate Square POS payment link'));

      const ua = String(navigator?.userAgent || '').toLowerCase();
      const isAndroid = ua.includes('android');
      const isIOS = /iphone|ipad|ipod/.test(ua) || (ua.includes('macintosh') && Number(navigator?.maxTouchPoints || 0) > 1);
      if (!isAndroid && !isIOS) {
        setError('Square POS can only be opened from an iPad/iPhone or Android device with the Square POS app installed. Open this page on the POS iPad and try again.');
        return;
      }

      const deepLinkUrl = isAndroid ? String(data?.android_url || '') : String(data?.ios_url || '');
      if (!deepLinkUrl) throw new Error('Missing deep-link URL from server');

      setStatusMessage('Opening Square POS…');
      window.location.assign(deepLinkUrl);
    } catch (e) {
      setError(e?.message || 'Failed to start Square POS payment');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Square Terminal (card-present) collection
  // ---------------------------------------------------------------------------
  const stopTerminalPolling = () => {
    if (terminalPollRef.current) {
      try {
        window.clearInterval(terminalPollRef.current);
      } catch {
        // ignore
      }
      terminalPollRef.current = null;
    }
  };

  useEffect(() => stopTerminalPolling, []);

  const stopCloverTerminalPolling = () => {
    if (cloverTerminalPollRef.current) {
      try {
        window.clearInterval(cloverTerminalPollRef.current);
      } catch {
        // ignore
      }
      cloverTerminalPollRef.current = null;
    }
  };

  useEffect(() => stopCloverTerminalPolling, []);

  const loadSquareTerminalPairing = async () => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/device-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Not connected to Square, wrong provider, or no permission — all mean "no
        // card-present button". Deliberately silent: this is a capability probe, not an
        // action the cashier asked for, so it must never raise an error banner.
        setSquareTerminalPaired(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setSquareTerminalPaired(Boolean(data?.paired));
    } catch {
      setSquareTerminalPaired(false);
    }
  };

  useEffect(() => {
    loadSquareTerminalPairing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelTerminalCharge = async () => {
    const checkoutId = String(terminalCharge?.checkoutId || '').trim();
    stopTerminalPolling();
    if (!checkoutId || !API_BASE_URL) {
      setTerminalCharge(null);
      return;
    }
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/checkout/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkout_id: checkoutId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel the Terminal checkout'));
      setStatusMessage('Terminal checkout cancelled. The device has stopped prompting.');
    } catch (e) {
      setError(e?.message || 'Failed to cancel the Terminal checkout');
    } finally {
      setTerminalCharge(null);
    }
  };

  const chargeOnSquareTerminal = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;
    if (terminalCharge) {
      setError('A Terminal checkout is already in progress. Wait for it to finish or cancel it first.');
      return;
    }

    // Same safeguard as "Mark as Paid" / "Take payment" (R8.1, R8.2): this pushes a
    // specific amount to a physical card reader, so settling the wrong party's ticket
    // here charges the wrong group.
    if (!confirmPartySettlement(order)) return;

    const orderNumber = String(order?.order_number || '').trim();
    const key = orderKey(order);

    try {
      setBusy(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: order?.order_timestamp,
          // The amount is ALWAYS re-read from the stored order server-side; nothing about
          // the price is sent from here. Tipping on the device is requested when the tenant
          // enabled Square Terminal tips (squareTerminalTipsEnabled — its OWN flag, not the
          // Stripe-reader tip toggle). The server additionally refuses it when the guest
          // already tipped online, and independently ORs in the setting, so nobody is ever
          // asked twice and enabling the setting takes effect even before this rebuild ships.
          allow_tipping: Boolean(squareTerminalTipsEnabledSetting),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to start the Terminal checkout'));

      const checkoutId = String(data?.checkout_id || '').trim();
      setTerminalCharge({
        key,
        orderNumber,
        checkoutId,
        status: String(data?.status || 'PENDING').toUpperCase(),
      });
      setStatusMessage(`Waiting for the card on the Square Terminal for order ${orderNumber}…`);

      if (!checkoutId) return;

      // Poll the checkout rather than waiting on the webhook, so the cashier sees the
      // outcome at the counter. The order is marked PAID only by the payment webhook —
      // this poll never claims money itself.
      stopTerminalPolling();
      const startedAt = Date.now();
      terminalPollRef.current = window.setInterval(async () => {
        try {
          const s = await Auth.currentSession();
          const t = s.getIdToken().getJwtToken();
          const qs = new URLSearchParams();
          qs.set('checkout_id', checkoutId);
          const r = await fetch(`${API_BASE_URL}/admin/pos/terminal/checkout/status?${qs.toString()}`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) return; // transient; keep polling until the deadline

          const st = String(d?.status || '').toUpperCase();
          setTerminalCharge((prev) => (prev && prev.checkoutId === checkoutId ? { ...prev, status: st } : prev));

          if (st === 'COMPLETED') {
            stopTerminalPolling();
            setTerminalCharge(null);
            setStatusMessage(
              `Card approved on the Terminal for order ${orderNumber}. Square is settling the payment — the ticket flips to Paid when Square confirms it.`
            );
            // AUTO-ADVANCE fulfillment. The customer taps their card when ready to complete,
            // so a successful terminal payment IS the completion — no separate button press.
            // Dine-in -> DINE_IN_COMPLETE, pickup/to-go/pay-at-store -> PICKUP_COMPLETE,
            // delivery -> completeStatus "" (skip; a counter payment does not complete a
            // delivery). Best-effort: the payment already succeeded, so a failed fulfillment
            // push must not surface as a payment error — the fulfillment chips remain.
            try {
              const { completeStatus } = squareTerminalCompletion(order);
              if (completeStatus) {
                const s2 = await Auth.currentSession();
                const t2 = s2.getIdToken().getJwtToken();
                await fetch(`${API_BASE_URL}/admin/pos/orders/fulfillment/update`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${t2}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    order_number: orderNumber,
                    order_timestamp: order?.order_timestamp,
                    adminFulfillmentStatus: completeStatus,
                  }),
                });
              }
            } catch {
              // Fulfillment auto-advance is best-effort; the payment is done regardless.
            }
            try {
              await fetchQueue({ reset: true });
            } catch {
              // ignore
            }
          } else if (st === 'CANCELED' || st === 'CANCEL_REQUESTED') {
            stopTerminalPolling();
            setTerminalCharge(null);
            const reason = String(d?.cancel_reason || '').trim();
            setStatusMessage(
              `Terminal checkout for order ${orderNumber} was cancelled${reason ? ` (${reason})` : ''}. The ticket is still unpaid.`
            );
          }

          // Square expires the checkout on its own (deadline_duration), but stop polling
          // so a forgotten tab does not hit the API forever.
          if (Date.now() - startedAt > 8 * 60 * 1000) {
            stopTerminalPolling();
            setTerminalCharge(null);
          }
        } catch {
          // ignore a single failed poll
        }
      }, 3000);
    } catch (e) {
      setError(e?.message || 'Failed to start the Terminal checkout');
      setTerminalCharge(null);
    } finally {
      setBusy(false);
    }
  };

  // ---- SHIFT4 Terminal Charge (shift4-payment-integration) ----------------------------
  // Capability probe: is Shift4 connected + a terminal paired for this tenant? Silent like
  // the Clover/Square probes — a non-Shift4 tenant, an unconnected one, or a 403 all mean
  // "no button", so nothing is ever shown or errors for another provider's tenant.
  const loadShift4TerminalPairing = async () => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/connect/shift4/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setShift4TerminalPaired(false); return; }
      const data = await res.json().catch(() => ({}));
      // Button needs BOTH a connection and a paired terminal (card-present).
      setShift4TerminalPaired(Boolean(data?.connected) && Boolean(data?.paired));
    } catch {
      setShift4TerminalPaired(false);
    }
  };

  // Charge a Shift4 order on the paired terminal. SYNCHRONOUS (no PENDING poll): the POST
  // returns approved/declined directly. Amount is re-priced server-side; the client sends no
  // price. On approval the server marks the order paid + auto-completes a pickup, so we just
  // refresh the queue. Fully isolated: only ever called for a SHIFT4 order via the gated button.
  const chargeOnShift4Terminal = async (order) => {
    if (!API_BASE_URL) { setError('Missing API base URL (REACT_APP_ADMIN_API_URL).'); return; }
    if (busy || shift4Busy) return;
    // Same wrong-party safeguard as the Square/Clover charge buttons.
    if (!confirmPartySettlement(order)) return;
    const orderNumber = String(order?.order_number || '').trim();
    try {
      setShift4Busy(true);
      setBusy(true);
      setError('');
      setStatusMessage(`Charging on the Shift4 terminal for order ${orderNumber}…`);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/shift4/terminal/charge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber, order_timestamp: order?.order_timestamp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to charge on the Shift4 terminal'));
      if (data?.result === 'approved') {
        setStatusMessage(`Card approved on the Shift4 terminal for order ${orderNumber}. The ticket is now Paid.`);
        try { await fetchQueue({ reset: true }); } catch { /* ignore */ }
      } else {
        const result = String(data?.result || 'declined').toUpperCase();
        setStatusMessage(`Shift4 terminal did not approve order ${orderNumber} (${result}). The ticket is still unpaid.`);
      }
    } catch (e) {
      setError(e?.message || 'Failed to charge on the Shift4 terminal');
    } finally {
      setShift4Busy(false);
      setBusy(false);
    }
  };

  // ---- CLOVER Terminal Charge (clover-terminal-charge) --------------------------------
  // Capability probe: is a Clover terminal paired for this tenant? Silent like the Square
  // probe — a non-Clover tenant, an unpaired one, or a 403 all mean "no button".
  const loadCloverTerminalPairing = async () => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setCloverTerminalPaired(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setCloverTerminalPaired(Boolean(data?.paired));
    } catch {
      setCloverTerminalPaired(false);
    }
  };

  useEffect(() => {
    loadCloverTerminalPairing();
    loadShift4TerminalPairing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel an in-flight Clover charge so the device stops prompting. The charge request
  // itself is a blocking HTTP call, so this races it: the device returns to idle and the
  // order stays unpaid regardless of which resolves first.
  const cancelCloverTerminalCharge = async () => {
    const inFlight = cloverTerminalCharge;
    stopCloverTerminalPolling();
    if (!API_BASE_URL || !inFlight) {
      setCloverTerminalCharge(null);
      return;
    }
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/checkout/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: inFlight.orderNumber,
          order_timestamp: inFlight.orderTimestamp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel the Clover charge'));
      setStatusMessage('Clover charge cancelled. The device has stopped prompting.');
    } catch (e) {
      setError(e?.message || 'Failed to cancel the Clover charge');
    } finally {
      setCloverTerminalCharge(null);
    }
  };

  const chargeOnCloverTerminal = async (order, tipMode = '') => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;
    if (cloverTerminalCharge) {
      setError('A Clover charge is already in progress. Wait for it to finish or cancel it first.');
      return;
    }

    // Same wrong-party safeguard as Square terminal / Mark as Paid.
    if (!confirmPartySettlement(order)) return;

    const orderNumber = String(order?.order_number || '').trim();
    const key = orderKey(order);

    // ON-BILL tip: staff enter the written tip on the iPad BEFORE charging. Prompt for it
    // here, validate to a non-negative cents integer, and pass it through; the reader is not
    // asked to collect a tip in this mode. A blank/cancelled entry means "no tip".
    let onBillTipCents = 0;
    if (tipMode === 'on_bill') {
      const raw = window.prompt(`Tip amount for order ${orderNumber} (in dollars, e.g. 2.50). Leave blank for no tip.`, '');
      if (raw === null) return; // staff cancelled the whole action
      const dollars = String(raw).trim();
      if (dollars) {
        const n = Number(dollars);
        if (!Number.isFinite(n) || n < 0) {
          setError('Enter a valid tip amount (a non-negative number).');
          return;
        }
        onBillTipCents = Math.round(n * 100);
      }
    }

    // Auto-advance fulfillment on a successful charge (dine-in -> DINE_IN_COMPLETE,
    // pickup/to-go/pay-at-store -> PICKUP_COMPLETE, delivery -> skip). Best-effort: the
    // payment already succeeded, so a failed fulfillment push must not surface as an error.
    // Factored out so both the fast-path (charge decided before the poll starts) and the
    // poll's SUCCESS branch reuse it.
    const autoAdvanceFulfillment = async () => {
      try {
        const { completeStatus } = squareTerminalCompletion(order);
        if (completeStatus) {
          const s2 = await Auth.currentSession();
          const t2 = s2.getIdToken().getJwtToken();
          await fetch(`${API_BASE_URL}/admin/pos/orders/fulfillment/update`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${t2}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_number: orderNumber,
              order_timestamp: order?.order_timestamp,
              adminFulfillmentStatus: completeStatus,
            }),
          });
        }
      } catch {
        // fulfillment auto-advance is best-effort; the payment is done regardless.
      }
    };

    try {
      setBusy(true);
      setError('');
      setStatusMessage('');
      setCloverTerminalCharge({ key, orderNumber, orderTimestamp: order?.order_timestamp });
      setStatusMessage(`Waiting for the card on the Clover terminal for order ${orderNumber}…`);

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // ASYNC START: this POST returns 202 {status:'PENDING'} almost immediately. The
      // backend runs the blocking card-present charge OFF the request path (a self-invoke)
      // because holding the tap+sign window open synchronously exceeds API Gateway's ~29s
      // integration timeout. Amount is re-priced server-side; the client sends no price.
      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: order?.order_timestamp,
          // Explicit tip method chosen by the button the staff tapped. The server honors
          // this over its priority fallback: 'on_reader' => device prompts the customer;
          // 'on_bill' => charge includes the staff-entered tip below, no device prompt.
          ...(tipMode ? { tip_mode: tipMode } : {}),
          ...(tipMode === 'on_bill' ? { tip_cents: onBillTipCents } : {}),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to charge on the Clover terminal'));

      // A charge that somehow already resolved synchronously (approved true/false, not the
      // 202 PENDING). Kept for forward/backward compatibility with the response shape.
      if (data?.approved === true) {
        setStatusMessage(`Card approved on the Clover terminal for order ${orderNumber}. The ticket is now Paid.`);
        await autoAdvanceFulfillment();
        try { await fetchQueue({ reset: true }); } catch { /* ignore */ }
        setCloverTerminalCharge(null);
        return;
      }
      if (data?.approved === false) {
        const result = String(data?.result || 'DECLINED').toUpperCase();
        setStatusMessage(`Clover terminal did not approve order ${orderNumber} (${result}). The ticket is still unpaid.`);
        setCloverTerminalCharge(null);
        return;
      }

      // PENDING (202): the device is prompting. Poll the status endpoint until the worker
      // (or the payment webhook) records the outcome. The order is marked PAID only by the
      // backend — this poll never claims money itself; it reflects state for the cashier.
      stopCloverTerminalPolling();
      const startedAt = Date.now();
      cloverTerminalPollRef.current = window.setInterval(async () => {
        try {
          const s = await Auth.currentSession();
          const t = s.getIdToken().getJwtToken();
          const qs = new URLSearchParams();
          qs.set('order_number', orderNumber);
          qs.set('order_timestamp', String(order?.order_timestamp || ''));
          const r = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/checkout/status?${qs.toString()}`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) return; // transient; keep polling until the deadline

          const st = String(d?.checkout_status || '').toUpperCase();
          const paid = String(d?.payment_status || '').toLowerCase() === 'paid';

          if (st === 'SUCCESS' || paid) {
            stopCloverTerminalPolling();
            setCloverTerminalCharge(null);
            setStatusMessage(`Card approved on the Clover terminal for order ${orderNumber}. The ticket is now Paid.`);
            await autoAdvanceFulfillment();
            try { await fetchQueue({ reset: true }); } catch { /* ignore */ }
          } else if (st === 'DECLINED' || st === 'CANCELED' || st === 'FAILED') {
            stopCloverTerminalPolling();
            setCloverTerminalCharge(null);
            setStatusMessage(
              `Clover terminal did not approve order ${orderNumber} (${st}). The ticket is still unpaid.`
            );
          }

          // Safety cap so a forgotten tab does not poll forever. Comfortably past the
          // backend stale window (~150s) + card-presentation window.
          if (Date.now() - startedAt > 4 * 60 * 1000) {
            stopCloverTerminalPolling();
            setCloverTerminalCharge(null);
            setStatusMessage(
              `Still waiting on the Clover terminal for order ${orderNumber}. Check the device, then refresh the queue.`
            );
          }
        } catch {
          // ignore a single failed poll
        }
      }, 3000);
    } catch (e) {
      stopCloverTerminalPolling();
      setError(e?.message || 'Failed to charge on the Clover terminal');
      setCloverTerminalCharge(null);
    } finally {
      // NOTE: do NOT clear cloverTerminalCharge here — a PENDING charge stays "in flight"
      // (showing the Cancel button) until the poll resolves it. Only release the busy lock.
      setBusy(false);
    }
  };

  // Render the Clover charge button(s) for a ticket, mirroring the Stripe reader tip-method
  // model: each ENABLED tip method surfaces as its own button so staff pick per sale.
  //   - both on-reader + on-bill  -> two buttons ("Charge + customer tip" / "Charge + bill tip")
  //   - only one enabled          -> a single labelled button for that method
  //   - neither (tips off)        -> a single plain "Charge on Clover terminal"
  // The chosen mode is passed to chargeOnCloverTerminal, which sends tip_mode so the server
  // honors the button choice instead of falling back to its priority default.
  const renderCloverChargeButtons = (o, isCancelled) => {
    const disabled = busy || loadingMore || isCancelled || Boolean(cloverTerminalCharge);
    const bothOn = cloverTipOnReaderSetting && cloverTipOnBillSetting;
    if (bothOn) {
      return (
        <>
          <button
            type="button"
            className="btn-primary"
            onClick={() => chargeOnCloverTerminal(o, 'on_reader')}
            disabled={disabled}
            style={{ width: '100%', marginTop: 10 }}
          >
            Charge + customer tip on reader
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => chargeOnCloverTerminal(o, 'on_bill')}
            disabled={disabled}
            style={{ width: '100%', marginTop: 8 }}
          >
            Charge + bill tip
          </button>
          <div className="field-hint" style={{ marginTop: 6 }}>
            "Customer tip on reader" asks the guest to tip on the Clover device. "Bill tip"
            lets you enter the written tip before charging (dine-in). Either way the ticket
            flips to Paid when the card is approved.
          </div>
        </>
      );
    }
    if (cloverTipOnReaderSetting || cloverTipOnBillSetting) {
      const mode = cloverTipOnReaderSetting ? 'on_reader' : 'on_bill';
      const label = cloverTipOnReaderSetting ? 'Charge + customer tip on reader' : 'Charge + bill tip';
      const hint = cloverTipOnReaderSetting
        ? 'Asks the guest to tip on the paired Clover terminal, then charges. The ticket flips to Paid when the card is approved.'
        : 'Enter the written tip before charging, then prompts the guest to tap, insert or swipe. The ticket flips to Paid when the card is approved.';
      return (
        <>
          <button
            type="button"
            className="btn-primary"
            onClick={() => chargeOnCloverTerminal(o, mode)}
            disabled={disabled}
            style={{ width: '100%', marginTop: 10 }}
          >
            {label}
          </button>
          <div className="field-hint" style={{ marginTop: 6 }}>{hint}</div>
        </>
      );
    }
    return (
      <>
        <button
          type="button"
          className="btn-primary"
          onClick={() => chargeOnCloverTerminal(o)}
          disabled={disabled}
          style={{ width: '100%', marginTop: 10 }}
        >
          Charge on Clover terminal
        </button>
        <div className="field-hint" style={{ marginTop: 6 }}>
          Prompts the guest to tap, insert or swipe on the paired Clover
          terminal. The ticket flips to Paid as soon as the card is approved.
        </div>
      </>
    );
  };

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  const hasMore = useMemo(() => Boolean(cursor), [cursor]);

  const orderKey = (order) => {
    const orderNumber = String(order?.order_number || '').trim();
    const ts = String(order?.order_timestamp || '');
    return `${orderNumber}-${ts}`;
  };

  const fetchQueue = async ({ reset, background, loadAll } = { reset: false, background: false, loadAll: false }) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (background) {
      if (autoRefreshInFlightRef.current) return;
      if (busy || loadingMore) return;
    } else {
      if (busy && !reset) return;
    }

    try {
      if (background) {
        autoRefreshInFlightRef.current = true;
      } else if (reset) {
        setBusy(true);
      } else {
        setLoadingMore(true);
      }

      if (!background) {
        setError('');
        setStatusMessage('');
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const shouldLoadAll = (Boolean(loadAll) || (Boolean(reset) && !background)) && !loadingMore;
      const pageLimit = 200;
      const maxPages = 10;
      const maxOrders = 2000;
      const startedAt = Date.now();
      const maxMs = 15_000;

      const fetchPage = async ({ pageCursor, includeCursor }) => {
        const url = new URL(`${API_BASE_URL}/admin/pos/orders/queue`);
        url.searchParams.set('lookback_days', String(lookbackDays));
        url.searchParams.set('payment_status', String(paymentStatusFilter || 'paid'));
        url.searchParams.set('sales_channel', String(salesChannelFilter || 'pos'));
        // Map the dropdown selection onto the server's three buckets: active | cancelled | all.
        //
        // A FULFILLMENT-SPECIFIC SELECTION MUST REQUEST `all`, NOT `active`. The server's
        // `active` bucket now excludes finished orders (delivered / picked up / completed) --
        // that is the fix for completed orders sitting in the ACTIVE view. But this screen
        // narrows to a specific status CLIENT-side, by exact label match, over whatever the
        // server returned. So asking for `active` and then filtering for DELIVERED would ask
        // the server to omit precisely the rows the filter wants and return nothing.
        //
        // `active` and `all` are the only selections the server can satisfy directly; every
        // named status is a client-side narrowing of `all`.
        const orderStatusRaw = String(orderStatusFilter || '').trim();
        const orderStatusParam =
          orderStatusRaw.toLowerCase() === 'all'
            ? 'all'
            : orderStatusRaw.toUpperCase() === 'CANCELLED'
              ? 'cancelled'
              : orderStatusRaw.toLowerCase() === 'active'
                ? 'active'
                : 'all';
        url.searchParams.set('order_status', orderStatusParam);
        url.searchParams.set('limit', String(pageLimit));
        if (locationFilter && locationFilter !== 'all') {
          url.searchParams.set('location_id', String(locationFilter));
        }
        if (includeCursor && pageCursor) {
          url.searchParams.set('cursor', pageCursor);
        }

        const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to load POS queue');
        // The queue endpoint (authorized under "POS Queue: View") carries the SLA
        // config so POS-only users get it without "General Settings: View".
        if (data && data.posSla && typeof data.posSla === 'object') {
          setSlaConfig({
            enabled: Boolean(data.posSla.enabled),
            greenMinutes: Number(data.posSla.greenMinutes),
            orangeMinutes: Number(data.posSla.orangeMinutes),
            redMinutes: Number(data.posSla.redMinutes),
          });
        }
        // Table party subdivision gate, riding along for the same reason posSla does.
        // Only a page that actually CARRIES the key may set it: the backend omits it
        // entirely for a tenant with no stored block, and pages of one paginated read
        // must not disagree. `tablePartiesEnabled` is fail-closed, so a malformed or
        // absent block resolves false (R1.11).
        if (data && Object.prototype.hasOwnProperty.call(data, 'tableParties')) {
          setPartiesEnabled(tablePartiesEnabled(data));
        }
        // In-app card payment gate, riding along on the queue response for the SAME
        // permission reason as tableParties above: a POS-only cashier's /admin/general
        // 403s, so the queue response is the only place they can resolve whether the
        // "Charge card" button is offered. Only a page that CARRIES the key may set it,
        // matching tableParties/posSla, so one page of a paginated read cannot flip the
        // gate off. The backend omits the key entirely for a tenant that never opted in,
        // so a non-opted-in tenant stays false (fail-closed, R1.11 discipline).
        if (data && Object.prototype.hasOwnProperty.call(data, 'inAppCardPaymentEnabled')) {
          const on = data.inAppCardPaymentEnabled === true;
          inAppCardEnabledRef.current = on;
          setInAppCardEnabled(on);
        }
        // Razorpay UPI at-store QR gate, riding along the same way and for the same
        // permission reason: the "Collect UPI" button is decided here, not at /admin/general.
        // Only a page that CARRIES the key may set it; the backend omits it for a
        // non-opted-in tenant, so it stays false (fail-closed).
        if (data && Object.prototype.hasOwnProperty.call(data, 'razorpayUpiAtStoreEnabled')) {
          const upiOn = data.razorpayUpiAtStoreEnabled === true;
          razorpayUpiEnabledRef.current = upiOn;
          setRazorpayUpiEnabled(upiOn);
        }
        // Tenant POS provider for the in-app card gate, carried the same way and for the
        // same reason: an in-store/staff ticket carries no provider of its own.
        if (data && Object.prototype.hasOwnProperty.call(data, 'posProvider')) {
          const prov = String(data.posProvider || '').trim().toUpperCase();
          posTenantProviderRef.current = prov;
          setPosTenantProvider(prov);
        }
        // Scheduled-orders release lead, carried for the same permission reason as posSla:
        // a POS-only cashier cannot read /admin/general. The backend omits the key for a
        // tenant with no stored block, so a non-opted-in tenant keeps 0 (fail-closed).
        if (data && data.scheduledOrders && typeof data.scheduledOrders === 'object') {
          const nextRelease = Number(data.scheduledOrders.posReleaseMinutes) || 0;
          scheduledReleaseMinutesRef.current = nextRelease;
          setScheduledReleaseMinutes(nextRelease);
        }
        // POS printing config, riding along for the SAME permission reason.
        //
        // THIS IS THE FIX FOR AUTO-PRINT SILENTLY NEVER RUNNING. Both settings were
        // read only from `/admin/general`, which requires "General Settings: View".
        // A POS-only cashier gets a 403 there, `loadPrintingSettings` returns early
        // without surfacing anything, and `autoPrintNewOrdersEnabled` stayed false —
        // so auto-print never fired while manual print (which does not consult the
        // flag) kept working. The queue endpoint is authorized under "POS Queue: View",
        // so reading it here resolves for every role that can see this screen.
        if (data && data.posPrinting && typeof data.posPrinting === 'object') {
          setAutoPrintNewOrdersEnabled(Boolean(data.posPrinting.autoPrintNewOrders));
          // Same permission story as autoPrintNewOrders above: the person who settles a
          // payment at the counter is a POS-only cashier whose /admin/general read 403s,
          // so the receipt-on-payment flag has to ride the queue response too. Without
          // this line it stayed false for exactly that role and no receipt ever printed.
          setAutoPrintReceiptOnPayment(Boolean(data.posPrinting.autoPrintReceiptOnPayment));
          setPrintPaperSize(normalizePaperSize(data.posPrinting.paperSize));
        }
        // Tenant display timezone, riding along for the same permission reason: a POS-only
        // cashier's /admin/general call 403s, and this queue's "today" filter and printed
        // tickets both need the zone. `setTenantTimezone` ignores a payload that carries
        // none, so the backend's omission for an unconfigured tenant leaves Eastern in
        // place and one page of a paginated read cannot stomp another's value.
        setTenantTimezone(data);
        return data;
      };

      let aggregated = [];
      let nextCursor = '';
      let fetchedAny = false;

      if (shouldLoadAll && reset) {
        setOrders([]);
        setCursor('');
        setNewOrderUntil({});
        if (!background) {
          setStatusMessage('Loading…');
        }

        for (let page = 0; page < maxPages; page += 1) {
          if (Date.now() - startedAt > maxMs) break;
          if (aggregated.length >= maxOrders) break;

          const data = await fetchPage({ pageCursor: nextCursor, includeCursor: page > 0 });
          const list = Array.isArray(data?.orders) ? data.orders : [];
          fetchedAny = true;

          aggregated = [...aggregated, ...list];
          nextCursor = String(data?.next_cursor || '');
          if (!background) {
            setStatusMessage(`Loaded ${aggregated.length} order(s)…`);
          }

          if (!nextCursor || list.length === 0) break;
        }
      } else {
        const includeCursor = !reset && cursor;
        const data = await fetchPage({ pageCursor: cursor, includeCursor });
        const list = Array.isArray(data?.orders) ? data.orders : [];
        fetchedAny = true;
        aggregated = reset ? list : [...(orders || []), ...list];
        nextCursor = String(data?.next_cursor || '');
      }

      const list = fetchedAny ? aggregated : [];
      const nextOrders = list;

      try {
        setNewOrderUntil((prev) => {
          const now = Date.now();
          const out = {};
          for (const [k, until] of Object.entries(prev || {})) {
            if (Number(until) > now) out[k] = until;
          }
          return out;
        });
      } catch {
        // ignore
      }

      try {
        if (reset) {
          const nextKeys = new Set(nextOrders.map(orderKey));
          const prevKeys = prevQueueKeysRef.current || new Set();

          if (hasLoadedQueueOnceRef.current) {
            const newKeys = [];
            for (const k of nextKeys) {
              if (!prevKeys.has(k)) newKeys.push(k);
            }
            if (newKeys.length) {
              // Auto-print a genuinely NEW arrival: an order whose timestamp is
              // newer than the newest order we'd already seen. This is independent
              // of the fetch path (background poll or user-initiated refresh) and
              // does NOT fire for old orders that merely become visible after a
              // filter change / status change (their timestamp is not newer than
              // the baseline), which was the source of the spurious print dialogs.
              const prevMaxTs = Number(maxSeenOrderTsRef.current) || 0;
              if (autoPrintNewOrdersEnabled) {
                try {
                  const toPrint = [];
                  for (const k of newKeys) {
                    if (autoPrintedKeysRef.current?.has?.(k)) continue;
                    const o = nextOrders.find((x) => orderKey(x) === k);
                    if (!o) continue;
                    const ts = Number(o?.order_timestamp) || 0;
                    if (ts <= prevMaxTs) continue; // old order surfaced (filter/status change) — not a new arrival
                    autoPrintedKeysRef.current.add(k);
                    toPrint.push(o);
                  }
                  if (toPrint.length) {
                    console.log(`[AUTO-PRINT] Printing ${toPrint.length} new order(s)`);
                    window.setTimeout(() => {
                      try {
                        for (const o of toPrint) {
                          const orderNum = String(o?.order_number || '').trim();
                          console.log(`[AUTO-PRINT] Printing order: ${orderNum}`);
                          // Auto-print always produces the kitchen TICKET (no prices),
                          // at the tenant's selected paper size.
                          onPrintOrder(o, { silent: true, contentType: 'ticket' });
                        }
                      } catch (err) {
                        console.error('[AUTO-PRINT] Error printing orders:', err);
                      }
                    }, 50);
                  } else {
                    console.log('[AUTO-PRINT] No genuinely new orders to print');
                  }
                } catch (err) {
                  console.error('[AUTO-PRINT] Error in auto-print logic:', err);
                }
              } else {
                console.log('[AUTO-PRINT] Feature disabled');
              }

              const expiresAt = Date.now() + 15_000;
              setNewOrderUntil((prev) => {
                const out = { ...(prev || {}) };
                for (const k of newKeys) {
                  if (!out[k]) out[k] = expiresAt;
                }
                return out;
              });

              window.setTimeout(() => {
                try {
                  setNewOrderUntil((prev) => {
                    const now = Date.now();
                    const out = {};
                    for (const [k, until] of Object.entries(prev || {})) {
                      if (Number(until) > now) out[k] = until;
                    }
                    return out;
                  });
                } catch {
                  // ignore
                }
              }, 15_100);
            }
          } else {
            hasLoadedQueueOnceRef.current = true;
          }

          prevQueueKeysRef.current = nextKeys;

          // Update the newest-timestamp baseline AFTER the new-arrival check above,
          // so the next fetch compares against what we knew before this one.
          try {
            let maxTs = Number(maxSeenOrderTsRef.current) || 0;
            for (const o of nextOrders) {
              const ts = Number(o?.order_timestamp) || 0;
              if (ts > maxTs) maxTs = ts;
            }
            maxSeenOrderTsRef.current = maxTs;
          } catch {
            // ignore
          }
        } else {
          prevQueueKeysRef.current = new Set(nextOrders.map(orderKey));
        }
      } catch {
        // ignore
      }

      setOrders(nextOrders);
      setCursor(String(nextCursor || ''));
      if (!background) {
        const tookMs = Date.now() - startedAt;
        const stoppedEarly = Boolean(nextCursor) && (nextOrders.length >= maxOrders || tookMs > maxMs);
        // Record the server-side facts only. The visible count is rendered from
        // `displayOrders.length` so it can never disagree with the listed rows -- see
        // `queueLoadInfo`. Clearing statusMessage hands the banner back to the load line
        // (an action message takes precedence until the next load).
        setQueueLoadInfo({ fetched: nextOrders.length, stoppedEarly });
        setStatusMessage('');
      }
    } catch (e) {
      if (!background) {
        setError(e?.message || 'Failed to load POS queue');
        if (reset) {
          setOrders([]);
          setCursor('');
          setNewOrderUntil({});
        }
      }
    } finally {
      if (background) {
        autoRefreshInFlightRef.current = false;
      } else {
        setBusy(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'POS Queue');
        if (!access.allowed) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await Promise.all([loadPrintingSettings(), loadPrintBranding(), loadLocations()]);
        await fetchQueue({ reset: true });

        try {
          const qs = new URLSearchParams(window.location.search || '');
          const ok = String(qs.get('ok') || '').toLowerCase();
          const orderNumber = String(qs.get('order_number') || '').trim();
          const err = String(qs.get('error_code') || '').trim();
          if (ok === 'true' && orderNumber) {
            setStatusMessage(`Payment recorded for order ${orderNumber}.`);
          } else if (ok === 'false' && (orderNumber || err)) {
            setError(`Payment not completed${orderNumber ? ` for order ${orderNumber}` : ''}${err ? ` (${err})` : ''}.`);
          }
        } catch {
          // ignore
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    fetchQueue({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookbackDays, paymentStatusFilter, orderStatusFilter, salesChannelFilter, locationFilter]);

  useEffect(() => {
    if (loading) return;
    if (dateMode !== 'preset') return;
    const next = presetKeyToDays(presetKey, customDays);
    if (next !== lookbackDays) setLookbackDays(next);
  }, [loading, dateMode, presetKey, customDays, lookbackDays]);

  useEffect(() => {
    if (loading) return;
    const intervalMs = 15_000;
    const id = window.setInterval(() => {
      try {
        if (detailsOrder) return;
        fetchQueue({ reset: true, background: true });
      } catch {
        // ignore
      }
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [loading, detailsOrder, lookbackDays, paymentStatusFilter, orderStatusFilter, salesChannelFilter, locationFilter]);

  useEffect(() => {
    if (dateMode === 'today') {
      setPresetKey('1d');
      setLookbackDays(1);
    }
  }, [dateMode]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-receipt"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>POS Queue</h1>
            <p>Paid online POS orders and readiness status</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* In-app card payment (in-app-card-payment spec). Mounted only while a "Charge
            card" session is active; unmounting on close disposes the Stripe Elements. On
            a paid result we surface a status line and refresh the queue so the settled
            ticket drops out, mirroring the mobile onChargeCardInApp completion. */}
        {chargeCardOrder ? (
          <StripeChargeCardModal
            order={chargeCardOrder}
            onClose={() => setChargeCardOrder(null)}
            onPaid={(paid) => {
              const on = String(chargeCardOrder?.order_number || '').trim();
              const paidOrderForPrint = chargeCardOrder;
              setChargeCardOrder(null);
              setError('');
              setStatusMessage(
                paid
                  ? `Order ${on} paid by card.`
                  : 'Payment submitted — confirming. This may take a moment.',
              );
              // Auto-print the customer receipt on a genuine paid card charge (counter), if
              // the tenant enabled it. Best-effort and silent.
              if (paid) maybeAutoPrintReceiptOnPayment(paidOrderForPrint);
              fetchQueue({ reset: true }).catch(() => {});
            }}
          />
        ) : null}

        {upiQrOrder ? (
          <RazorpayUpiQrModal
            order={upiQrOrder}
            onClose={() => setUpiQrOrder(null)}
            onPaid={(paid) => {
              // UPI settles asynchronously via the webhook; the modal calls this only once
              // its poll has observed the order flip to paid. Keep the modal open (it shows
              // the success state) and refresh the queue so the settled ticket drops out.
              const on = String(upiQrOrder?.order_number || '').trim();
              const paidOrderForPrint = upiQrOrder;
              setError('');
              setStatusMessage(
                paid
                  ? `Order ${on} paid by UPI.`
                  : 'Waiting for UPI payment — confirming. This may take a moment.',
              );
              if (paid) maybeAutoPrintReceiptOnPayment(paidOrderForPrint);
              fetchQueue({ reset: true }).catch(() => {});
            }}
          />
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {/* An explicit action message (e.g. "Updated fulfillment") wins; otherwise show the
            load line, whose visible count is `displayOrders.length` -- the very array mapped
            below, so the banner and the rows can never report different numbers. */}
        {statusMessage || queueLoadInfo ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage
              || formatQueueLoadLine({
                shown: displayOrders.length,
                fetched: queueLoadInfo.fetched,
                stoppedEarly: queueLoadInfo.stoppedEarly,
              })}
          </div>
        ) : null}

        <div className="products-list-container">
          <div className="pos-toolbar">
            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Channel</label>
              <select
                className="pos-input"
                value={salesChannelFilter}
                onChange={(e) => setSalesChannelFilter(String(e.target.value || 'all'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 160 }}
              >
                <option value="all">ALL</option>
                <option value="pos">POS</option>
                <option value="marketplace">MARKETPLACE</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Payment</label>
              <select
                className="pos-input"
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(String(e.target.value || 'any'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 160 }}
              >
                <option value="any">ALL</option>
                <option value="paid">PAID</option>
                <option value="pending_payment">PENDING</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Order</label>
              <select
                className="pos-input"
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(String(e.target.value || 'active'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 200 }}
              >
                <option value="active">ACTIVE</option>
                <option value="scheduled">SCHEDULED / UPCOMING</option>
                <option value="OPEN">OPEN</option>
                <option value="READY FOR PICKUP">READY FOR PICKUP</option>
                <option value="PICKUP COMPLETE">PICKUP COMPLETE</option>
                <option value="DINE-IN COMPLETE">DINE-IN COMPLETE</option>
                <option value="READY FOR DELIVERY">READY FOR DELIVERY</option>
                <option value="OUT FOR DELIVERY">OUT FOR DELIVERY</option>
                <option value="DELIVERED">DELIVERED</option>
                <option value="CANCELLED">CANCELLED</option>
                <option value="all">ALL</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Location</label>
              <select
                className="pos-input"
                value={locationFilter}
                onChange={(e) => setLocationFilter(String(e.target.value || 'all'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 200 }}
              >
                <option value="all">ALL LOCATIONS</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Date</label>
              <div className="pos-chip-row">
                <button
                  type="button"
                  className={`pos-chip ${dateMode === 'today' ? 'active' : ''}`}
                  onClick={() => setDateMode('today')}
                  disabled={busy || loadingMore}
                >
                  TODAY
                </button>
                <button
                  type="button"
                  className={`pos-chip ${dateMode === 'preset' ? 'active' : ''}`}
                  onClick={() => setDateMode('preset')}
                  disabled={busy || loadingMore}
                >
                  PRESET
                </button>
              </div>
            </div>

            {dateMode === 'preset' ? (
              <div className="pos-toolbar-group">
                <label className="pos-toolbar-label">Range</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="pos-input"
                    value={presetKey}
                    onChange={(e) => setPresetKey(String(e.target.value || '7d'))}
                    disabled={busy || loadingMore}
                    style={{ minWidth: 220 }}
                  >
                    <option value="1d">{presetKeyToLabel('1d')}</option>
                    <option value="7d">{presetKeyToLabel('7d')}</option>
                    <option value="1m">{presetKeyToLabel('1m')}</option>
                    <option value="6m">{presetKeyToLabel('6m')}</option>
                    <option value="ytd">{presetKeyToLabel('ytd')}</option>
                    <option value="all">{presetKeyToLabel('all')}</option>
                    <option value="custom">{presetKeyToLabel('custom')}</option>
                  </select>

                  {String(presetKey || '').trim().toLowerCase() === 'custom' ? (
                    <input
                      className="pos-input"
                      type="number"
                      min={1}
                      max={365}
                      value={customDays}
                      onChange={(e) => setCustomDays(String(e.target.value || '7'))}
                      disabled={busy || loadingMore}
                      style={{ minWidth: 140 }}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="pos-toolbar-group" style={{ minWidth: 240 }}>
              <label className="pos-toolbar-label">Search</label>
              <input
                className="pos-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order #, name, phone, email"
                disabled={busy || loadingMore}
              />
            </div>

            <div className="pos-toolbar-actions">
              <button type="button" className="btn-primary" onClick={() => fetchQueue({ reset: true })} disabled={busy || loadingMore}>
                Refresh
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fetchQueue({ reset: false })}
                disabled={busy || loadingMore || !hasMore}
              >
                Load more
              </button>
            </div>
          </div>

          {displayOrders.length === 0 ? (
            <div className="pos-empty">
              <div style={{ fontWeight: 900, color: '#0f172a' }}>No orders</div>
              <div className="field-hint">Try changing the lookback window or payment filter.</div>
            </div>
          ) : (
            <div className="pos-order-list">
              {/* `displayOrders` is `filteredOrders` itself when the party gate is off,
                  so a non-opted-in tenant renders the identical list in the identical
                  order (R1.8). */}
              {displayOrders.map((o) => {
                const orderNumber = String(o?.order_number || '');
                const k = `${String(orderNumber || '').trim()}-${String(o?.order_timestamp || '')}`;
                const isNew = Date.now() < Number(newOrderUntil?.[k] || 0);
                const placedAt = formatDateTimeInZone(o?.order_timestamp);
                // For a scheduled order the time that matters to staff is the SCHEDULED
                // time, not when it was placed. Show that prominently; keep the placed
                // time available as secondary context. ASAP orders are unchanged.
                const oIsScheduled = o?.isScheduled === true && Number.isFinite(Number(o?.scheduledTime));
                const scheduledAt = oIsScheduled ? formatDateTimeInZone(o.scheduledTime) : '';
                const ownTaxEnabled =
                  Boolean(o?.posTaxEnabled ?? posTaxEnabled) &&
                  String(o?.posTaxCollectionMode ?? posTaxCollectionMode ?? 'PROVIDER')
                    .trim()
                    .toUpperCase() === 'OWN';
                const ratePercent = Number(o?.posTaxRatePercent ?? posTaxRatePercent);

                const orderSubtotalCents = safeInt(o?.subtotal_cents, 0);
                const orderTaxCents = safeInt(o?.tax_cents, 0);
                const hasStoredBreakdown = Number.isFinite(Number(o?.subtotal_cents)) && Number.isFinite(Number(o?.tax_cents));

                const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(o?.amount_total_cents, 0);
                const rawTotalCents = safeInt(o?.amount_total_cents, 0);
                const taxFromStored = hasStoredBreakdown ? orderTaxCents : 0;
                const inclusive = isTaxInclusiveOrder(o);
                const discountCents = resolveDiscountCents({
                  order: o,
                  subtotalCents,
                  taxCents: taxFromStored,
                  rawTotalCents,
                  hasStoredBreakdown,
                });
                const taxableSubtotalCents = Math.max(0, subtotalCents - discountCents);
                const { taxCents: computedTaxCents, totalCents: computedTotalCents } = ownTaxEnabled
                  ? computeOwnTax(taxableSubtotalCents, ratePercent)
                  : { taxCents: 0, totalCents: taxableSubtotalCents };

                const taxCents = hasStoredBreakdown ? taxFromStored : computedTaxCents;
                // Inclusive orders already have tax inside the subtotal, so the
                // total is the discounted subtotal on its own.
                const totalCents = hasStoredBreakdown
                  ? (inclusive ? taxableSubtotalCents : taxableSubtotalCents + taxCents)
                  : computedTotalCents;

                const total = formatMoney(o?.posFinalTotalMoney, o?.amount_total_cents);
                const notes = String(o?.customerSubmittedSnapshot?.notes || '').trim();
                const customerName = String(o?.customer_name || '').trim();
                const customerEmail = String(o?.customer_email || '').trim();
                const customerPhone = String(o?.customer_phone || '').trim();
                const notifiedAt = o?.posPaidNotifiedAt ? formatDateTimeInZone(o.posPaidNotifiedAt) : '';
                const smsStatus = String(o?.posPaidNotifyResult?.sms || '');
                const emailStatus = String(o?.posPaidNotifyResult?.email || '');
                const paymentLinkUrl = String(o?.posPaymentLink?.href || o?.posPaymentLink?.long_url || o?.posPaymentLink?.url || '').trim();
                const paymentStatus = String(o?.payment_status || 'unknown');
                const posProvider = String(o?.posProvider || '').trim().toUpperCase();
                const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : posProvider === 'PHONEPE' ? 'PhonePe' : posProvider === 'RAZORPAY' ? 'Razorpay' : '';
                const salesChannel = String(o?.salesChannel || '').trim().toUpperCase();
                const marketplaceProvider = String(o?.marketplaceProvider || '').trim().toUpperCase();
                const isUberEatsOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'ubereats' ||
                  marketplaceProvider === 'UBEREATS';
                const isDoorDashOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'doordash' ||
                  marketplaceProvider === 'DOORDASH';
                const posOrderState = String(o?.posOrderState || o?.squareOrderState || '').trim();
                const rawFulfillment = String(o?.adminFulfillmentStatus || o?.posFulfillmentState || o?.squareFulfillmentState || '').trim();
                const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
                const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
                const paymentMode = String(o?.posPaymentMode || '').trim().toUpperCase();
                const canTakePayment =
                  posProvider === 'SQUARE' && paymentMode === 'PAY_AT_POS' && paymentStatus.toLowerCase() === 'pending_payment';
                // Card-present via the Terminal API. Same order conditions as
                // canTakePayment, plus a paired device — without one there is nothing to
                // push the checkout to, so the button would only ever 409 — plus the order
                // actually existing in Square. The server refuses a ticket with no
                // posOrderId (the settling payment could not be matched back to it), so
                // hiding the button here keeps that refusal out of the cashier's way.
                const canChargeOnSquareTerminal =
                  canTakePayment && squareTerminalPaired && Boolean(String(o?.posOrderId || '').trim());
                const terminalChargeActive = Boolean(terminalCharge) && terminalCharge?.key === orderKey(o);
                // CLOVER Terminal Charge (clover-terminal-charge): the Clover counterpart to
                // canChargeOnSquareTerminal. Same order conditions — a Clover PAY_AT_POS
                // ticket still pending payment, a paired Clover device, and a Clover order id
                // (posOrderId) so a settled payment can be matched back to the ticket.
                const canChargeOnCloverTerminal =
                  posProvider === 'CLOVER'
                  && paymentMode === 'PAY_AT_POS'
                  && paymentStatus.toLowerCase() === 'pending_payment'
                  && cloverTerminalPaired
                  && Boolean(String(o?.posOrderId || o?.cloverOrderId || '').trim());
                const cloverChargeActive = Boolean(cloverTerminalCharge) && cloverTerminalCharge?.key === orderKey(o);
                // SHIFT4 Terminal Charge (shift4-payment-integration). Same gate shape as the
                // Clover/Square buttons: a SHIFT4 PAY_AT_POS ticket still pending payment, with
                // Shift4 connected + a terminal paired. No posOrderId requirement — Shift4 has
                // no cloud order object (we reconcile by our order number as the invoice).
                // IN-STORE / staff tickets (dine-in, to-go) carry no posProvider of their own
                // — pos_order_core only stamps posProvider on storefront (non-staff) orders —
                // so fall back to the tenant provider, exactly like the in-app-card gate does.
                const effectiveProviderShift4 =
                  posProvider || posTenantProvider || posTenantProviderRef.current;
                const canChargeOnShift4Terminal =
                  effectiveProviderShift4 === 'SHIFT4'
                  && paymentMode === 'PAY_AT_POS'
                  && paymentStatus.toLowerCase() === 'pending_payment'
                  && shift4TerminalPaired;
                const canMarkAsPaid = paymentStatus.toLowerCase() !== 'paid' && paymentStatus.toLowerCase() !== 'refunded';
                // Razorpay UPI at-store QR (razorpay-upi-dinein-qr spec). Same gate as the
                // ungrouped list below; included here too because the location-GROUPED view
                // is a separate render path and would otherwise never show the button. Keyed
                // on the SEPARATE razorpayUpiAtStoreEnabled flag; requires a RAZORPAY + INR
                // unpaid order (tenant-provider fallback for in-store tickets with no provider).
                const canCollectUpi = canCollectUpiAtStore(o, {
                  razorpayUpiAtStoreEnabled: razorpayUpiEnabled || razorpayUpiEnabledRef.current,
                  posProvider: posTenantProvider || posTenantProviderRef.current,
                });
                const summaryPhone = customerPhone ? maskPhone(customerPhone) : '';

                const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
                const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED';
                const isPickupComplete = String(fulfillmentLabel || '').trim().toUpperCase() === 'PICKUP COMPLETE';
                const canCancel = !isCancelled && !isPickupComplete;

                // Uber Eats orders are pending/unaccepted until staff accept them.
                // Show accept/reject only while pending and not cancelled (Req 12.5).
                const canAcceptUberEats =
                  isUberEatsOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');

                // DoorDash orders are pending/unaccepted until staff accept them.
                // Show accept/reject only while pending and not cancelled (Req 15.6).
                const canAcceptDoorDash =
                  isDoorDashOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');
                const experienceFlag = String(o?.experience_flag || '').trim();

                // Single channel descriptor (lives in the subtitle, not as a loud badge).
                // Avoids the duplicate "UBEREATS / 🛵 UBER EATS" pair that cluttered the row.
                const channelLabel = isUberEatsOrder
                  ? 'Uber Eats'
                  : isDoorDashOrder
                    ? 'DoorDash'
                    : salesChannel === 'MARKETPLACE'
                      ? (marketplaceProvider || 'Marketplace')
                      : '';
                const channelColor = isUberEatsOrder ? '#06C167' : isDoorDashOrder ? '#FF3008' : '#6b7280';

                // Server Order Entry (staff) — additive, gated on presence.
                // Legacy/customer orders have no orderType, so these are empty
                // and render nothing (Property 3: additive isolation).
                const orderTypeRaw = String(o?.orderType || o?.customerSubmittedSnapshot?.orderType || '').trim().toUpperCase();
                // Dine-in is detected off orderType, NOT fulfillment_type: a dine-in
                // ticket carries fulfillment_type "pickup". Drives the dine-in chip set
                // and the "Paid and dine-in complete" combined button.
                const isDineIn = orderTypeRaw === 'DINE_IN';
                const orderTableLabel = String(o?.tableLabel || o?.customerSubmittedSnapshot?.tableLabel || '').trim();
                const orderServerName = String(o?.serverName || o?.customerSubmittedSnapshot?.serverName || '').trim();
                // Table party subdivision (R6.1, R6.2, R6.6, R6.7). Every one of these is
                // gated on `partiesEnabled`, not merely on the field being present.
                // `guestCount` is the reason that distinction matters: it is ALREADY
                // populated on existing orders, so presence-gating it would change the
                // card for tenants that never opted in — the conflict R1.4 resolves in
                // favour of leaving them alone.
                const orderPartyLabel = partiesEnabled ? partyLabelOf(o) : '';
                const orderPartyName = partiesEnabled ? partyNameOf(o) : '';
                const orderGuestCount = partiesEnabled ? safeInt(o?.guestCount, 0) : 0;
                const orderSharesTable = partiesEnabled && hasSharedTable(o, tableTicketCounts);
                const orderSharedTableCount = orderSharesTable
                  ? (tableTicketCounts.get(
                      normalizeTableLabel(o?.tableLabel || o?.customerSubmittedSnapshot?.tableLabel),
                    ) || 0)
                  : 0;
                const orderTypeBadge =
                  orderTypeRaw === 'DINE_IN'
                    ? (orderTableLabel
                      ? `DINE-IN · Table ${orderTableLabel}${orderPartyLabel ? ` · ${orderPartyLabel}` : ''}`
                      : 'DINE-IN')
                    : orderTypeRaw === 'TO_GO'
                      ? 'TO-GO'
                      : '';
                const orderTypeBadgeColor = orderTypeRaw === 'DINE_IN' ? 'var(--admin-brand, #7c3aed)' : orderTypeRaw === 'TO_GO' ? '#0891b2' : '#6b7280';

                // Order additions (order-additions R9.2 / R10.6). Derived and
                // presence-gated: an order with no `billPrintedAt` produces false and
                // renders nothing, so every pre-existing order is unchanged.
                //
                // THE "ADDED TO ×N" BADGE WAS REMOVED (owner request). It said an order
                // had been appended to, which is not actionable from the queue — the
                // amendment history is already in the expanded card and on the printed
                // chit. It only became visible once the queue response started carrying
                // `amendments`, and it made an already-crowded header row overflow,
                // clipping the one badge that IS actionable.
                const orderBillStale = isBillStale(o);

                // SLA blinker band for OPEN/ACTIVE orders (null when feature off / not OPEN).
                // Mirror the queue's notion of "open": explicit OPEN fulfillment, or a
                // pending-payment order that hasn't progressed to a later fulfillment state.
                // Treat any order without a later fulfillment status as OPEN for
                // SLA tracking, regardless of payment method (online-paid,
                // pay-at-store) or fulfillment type (pickup, delivery).
                // Completed/cancelled orders always carry a non-empty fulfillment
                // label (e.g. PICKUP COMPLETE, DELIVERED, CANCELLED), so they are
                // naturally excluded.
                const effectiveFulfillmentForSla = fulfillmentLabel || 'OPEN';
                // A still-held scheduled order has not entered the live queue yet, so it is
                // not "aging" and must not accrue an SLA band or an "OPEN for N minutes"
                // clock — that counts from placement and would show a 7:30 order as already
                // late at 5pm. Suppress SLA while held; once released (within
                // posReleaseMinutes of the slot) it ages normally like any other order.
                const heldScheduled = isHeldScheduledOrder({
                  isScheduled: o?.isScheduled,
                  scheduledTime: o?.scheduledTime,
                  posReleaseMinutes: scheduledReleaseMinutesRef.current || 0,
                  now: Math.floor(slaNowMs / 1000),
                });
                const slaBand = heldScheduled
                  ? null
                  : computeSlaBand(slaConfig, effectiveFulfillmentForSla, o?.order_timestamp, slaNowMs);
                const slaColor = slaBand === 'green' ? '#16a34a' : slaBand === 'orange' ? '#ea580c' : slaBand === 'red' ? '#dc2626' : '#6b7280';
                const slaOpenMinutes = slaBand
                  ? Math.max(0, Math.floor((slaNowMs / 1000 - Number(o?.order_timestamp)) / 60))
                  : null;

                return (
                  <details
                    key={`${orderNumber}-${String(o?.order_timestamp || '')}`}
                    className={`pos-order-disclosure${isNew ? ' pos-order-disclosure-new' : ''}`}
                  >
                    <summary className="pos-order-summary">
                      {slaBand ? (
                        <span
                          className={`pos-sla-light pos-sla-light-${slaBand}`}
                          title={`SLA: ${slaBand.toUpperCase()} — open ${slaConfig.greenMinutes}/${slaConfig.orangeMinutes}/${slaConfig.redMinutes} min thresholds`}
                          aria-label={`SLA ${slaBand}`}
                        />
                      ) : null}
                      <div className="pos-order-summary-main">
                        <button
                          type="button"
                          className="pos-order-summary-title-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDetailsOrder(o);
                          }}
                          disabled={!orderNumber}
                        >
                          {/* Shows the in-store token when the tenant issued one, and the
                              order number otherwise. `orderNumber` above is untouched: it
                              is still the React key, the disabled check, and what every
                              action on this row sends to the API. */}
                          {hasStoreTicketNumber(o) ? 'Ticket ' : 'Order '}
                          {orderDisplayNumber(o) || orderNumber}
                        </button>
                        <div className="pos-order-summary-sub">
                          {oIsScheduled ? (
                            <span style={{ fontWeight: 700, color: '#4c1d95' }}>
                              🕒 Scheduled: {scheduledAt}
                            </span>
                          ) : (
                            placedAt
                          )}
                          {channelLabel ? (
                            <span style={{ marginLeft: 8, fontWeight: 600, color: channelColor }}>· {channelLabel}</span>
                          ) : null}
                          <span style={{ marginLeft: 8, color: '#6b7280' }}>
                            · {oIsDelivery ? 'Delivery' : 'Pickup'}
                          </span>
                          {orderTypeBadge ? (
                            <span
                              style={{
                                marginLeft: 8,
                                fontWeight: 800,
                                fontSize: '0.6875rem',
                                letterSpacing: '0.02em',
                                color: '#fff',
                                background: orderTypeBadgeColor,
                                borderRadius: 6,
                                padding: '2px 8px',
                                textTransform: 'uppercase',
                              }}
                            >
                              {orderTypeBadge}
                            </span>
                          ) : null}
                        </div>
                        {/* THE MULTI-TICKET INDICATOR (R6.6). This is the single most
                            important thing this feature adds to the queue.
                            
                            The defect was never that a cashier read the wrong label — it
                            is that nothing on screen said there were two tickets at all,
                            so a cashier with one ticket in view behaved as though it were
                            the only one. On its own line and as a full sentence for the
                            same reason the stale-bill warning is: in the badge row it
                            would compete for space and clip mid-word. */}
                        {orderSharesTable ? (
                          <div
                            style={{
                              marginTop: 4,
                              padding: '4px 8px',
                              borderRadius: 6,
                              background: '#fffbeb',
                              border: '1px solid #fcd34d',
                              color: '#92400e',
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                            }}
                          >
                            Table {orderTableLabel} has {orderSharedTableCount} open tickets.
                            {orderPartyLabel ? ` This one is party ${orderPartyLabel}.` : ''}
                            {' '}Check the party before settling.
                          </div>
                        ) : null}
                        {orderPartyName ? (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--admin-brand-ink, #5b21b6)', fontWeight: 700, marginTop: 2 }}>
                            Party: {orderPartyLabel ? `${orderPartyLabel} · ` : ''}{orderPartyName}
                          </div>
                        ) : null}
                        {/* Stored on every dine-in order since server-order-entry and
                            displayed nowhere until now (R6.7). */}
                        {orderGuestCount > 0 ? (
                          <div style={{ fontSize: '0.8125rem', color: '#475569', fontWeight: 600, marginTop: 2 }}>
                            Guests: {orderGuestCount}
                          </div>
                        ) : null}
                        {orderServerName ? (
                          <div style={{ fontSize: '0.8125rem', color: '#475569', fontWeight: 600, marginTop: 2 }}>
                            Server: {orderServerName}
                          </div>
                        ) : null}
                        {/* On its OWN LINE, not in the badge row above. In that row it
                            competed with the order-type badge and the status pills and
                            got clipped mid-word ("BIL…"), which is worse than useless
                            for a warning. Full sentence rather than a cryptic pill,
                            since the whole point is telling someone what to do. */}
                        {orderBillStale ? (
                          <div
                            style={{
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              color: '#92400e',
                              marginTop: 4,
                            }}
                          >
                            ⚠ Bill out of date — items changed after it was printed. Print again before settling.
                          </div>
                        ) : null}
                        {slaBand ? (
                          <div className="pos-sla-msg" style={{ color: slaColor, fontWeight: 600, fontSize: '0.8125rem', marginTop: 2 }}>
                            Order has been in OPEN status for {slaOpenMinutes} {slaOpenMinutes === 1 ? 'minute' : 'minutes'}
                          </div>
                        ) : null}
                      </div>

                      <div className="pos-order-summary-customer">
                        <div className="pos-order-summary-customer-name">{customerName || '—'}</div>
                        {summaryPhone ? <div className="pos-order-summary-customer-phone">{summaryPhone}</div> : null}
                        {o?.fulfillment_type === 'delivery' && o?.delivery_address ? (
                          <div className="pos-order-summary-customer-phone" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>
                            {o.delivery_address.city}, {o.delivery_address.state}
                          </div>
                        ) : null}
                      </div>

                      <div className="pos-order-summary-badges">
                        <span className={`pos-badge ${paymentStatus.toLowerCase() === 'paid' ? 'pos-badge-paid' : 'pos-badge-neutral'}`}>
                          {paymentStatus}
                        </span>
                        {isDoorDashOrder && experienceFlag ? (
                          <span className="pos-badge pos-badge-neutral" title="DoorDash experience">
                            {experienceFlag}
                          </span>
                        ) : null}
                        {fulfillmentLabel ? <span className="pos-badge pos-badge-neutral">{fulfillmentLabel}</span> : null}
                        {o?.location_name ? (
                          <span className="pos-badge pos-badge-neutral" title={`Location: ${o.location_name}`}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>
                            {o.location_name}
                          </span>
                        ) : null}
                        {isNew ? <span className="pos-badge pos-badge-new">NEW</span> : null}
                        {/* Header PRINT button removed: printing now lives in the order
                            details as two explicit choices (Print Ticket / Print Receipt). */}
                    </div>

                      <div className="pos-order-summary-chevron" aria-hidden="true" />
                    </summary>

                    <div className="pos-order-details">
                      <div className="pos-order-card">
                        <div className="pos-order-meta">
                          <div>
                            <div className="pos-order-meta-label">{ownTaxEnabled ? 'Estimated total' : 'Total'}</div>
                            <div className={`pos-order-meta-value${ownTaxEnabled ? ' pos-order-total-highlight' : ''}`}>{total}</div>
                            {(() => {
                              const tipCents = safeInt(o?.tip_cents, 0);
                              // Show the tax line whenever the server actually stamped a tax
                              // (hasStoredBreakdown && taxCents > 0), NOT only when the order
                              // row happens to echo the ownTax flags. The stored tax is the
                              // authoritative figure; gating on ownTaxEnabled hid it for
                              // orders whose rows do not carry the tax-config flags.
                              const showStoredTax = hasStoredBreakdown && taxCents > 0;
                              const showBreakdown = showStoredTax || ownTaxEnabled || discountCents || tipCents > 0;
                              if (!showBreakdown) return null;
                              const taxLabel = `${resolveTaxLabel(o)}${inclusive ? ' (incl.)' : ''}`;
                              return (
                                <div className="pos-order-total-breakdown">
                                  <div className="pos-order-total-row">
                                    <span>Subtotal</span>
                                    <span>{formatMoney(null, subtotalCents)}</span>
                                  </div>
                                  {discountCents ? (
                                    <div className="pos-order-total-row">
                                      <span>Discount</span>
                                      <span>-{formatMoney(null, discountCents)}</span>
                                    </div>
                                  ) : null}
                                  {(showStoredTax || ownTaxEnabled) ? (
                                    <div className="pos-order-total-row">
                                      <span>{taxLabel}{ownTaxEnabled && !showStoredTax ? ` (${Number(ratePercent) || 0}%)` : ''}</span>
                                      <span>{formatMoney(null, taxCents)}</span>
                                    </div>
                                  ) : null}
                                  {tipCents > 0 ? (
                                    <div className="pos-order-total-row">
                                      <span>Tip</span>
                                      <span>{formatMoney(null, tipCents)}</span>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Customer</div>
                            <div className="pos-order-meta-value">{customerName || '—'}</div>
                            {customerPhone ? <div className="field-hint">Phone: {maskPhone(customerPhone)}</div> : null}
                            {customerEmail ? <div className="field-hint">Email: {customerEmail}</div> : null}
                          </div>
                          {!isUberEatsOrder && !isDoorDashOrder && o?.fulfillment_type === 'delivery' && o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-label">
                                <i className="fas fa-truck" style={{ marginRight: '6px', color: '#3b82f6' }}></i>
                                Delivery Address
                              </div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: '4px' }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.delivery_fee_cents > 0 ? (
                                <div className="field-hint" style={{ marginTop: '4px', color: '#059669' }}>
                                  <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                                  <strong>Delivery Fee:</strong> {formatMoney(null, o.delivery_fee_cents)}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {isUberEatsOrder ? (
                            <div style={{ gridColumn: '1 / -1', marginTop: 4, padding: 12, backgroundColor: '#E9FBF1', borderRadius: 8, border: '1px solid #06C167' }}>
                              <div style={{ fontWeight: 700, color: '#067D43', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🛵</span>
                                <span>Uber Eats Delivery Order</span>
                              </div>
                              {o?.delivery_address ? (
                                <div>
                                  <div className="pos-order-meta-value">
                                    {o.delivery_address.street}
                                    {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                                  </div>
                                  <div className="field-hint">
                                    {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                                  </div>
                                </div>
                              ) : null}
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.requested_delivery_time ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <strong>Requested:</strong> {formatDateTimeInZone(o.requested_delivery_time)}
                                </div>
                              ) : null}
                              {o?.ubereats_order_id ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                                  Uber Eats Order ID: {o.ubereats_order_id}
                                </div>
                              ) : null}
                              {canAcceptUberEats ? (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => acceptUberEatsOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ backgroundColor: '#06C167', borderColor: '#06C167' }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => rejectUberEatsOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {isDoorDashOrder ? (
                            <div style={{ gridColumn: '1 / -1', marginTop: 4, padding: 12, backgroundColor: '#FFF0ED', borderRadius: 8, border: '1px solid #FF3008' }}>
                              <div style={{ fontWeight: 700, color: '#B3210A', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🚗</span>
                                <span>DoorDash Delivery Order</span>
                                {experienceFlag ? (
                                  <span className="pos-badge pos-badge-neutral" style={{ marginLeft: 4 }}>{experienceFlag}</span>
                                ) : null}
                              </div>
                              {o?.delivery_address ? (
                                <div>
                                  <div className="pos-order-meta-value">
                                    {o.delivery_address.street}
                                    {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                                  </div>
                                  <div className="field-hint">
                                    {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                                  </div>
                                </div>
                              ) : null}
                              {o?.special_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Special Instructions:</strong> {o.special_instructions}
                                </div>
                              ) : null}
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.requested_delivery_time ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <strong>Requested:</strong> {formatDateTimeInZone(o.requested_delivery_time)}
                                </div>
                              ) : null}
                              {o?.merchant_tip > 0 ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#059669' }}>
                                  <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                                  <strong>Merchant Tip:</strong> {formatMoney(null, o.merchant_tip)}
                                </div>
                              ) : null}
                              {o?.doordash_order_id ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                                  DoorDash Order ID: {o.doordash_order_id}
                                </div>
                              ) : null}
                              {canAcceptDoorDash ? (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => acceptDoorDashOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ backgroundColor: '#FF3008', borderColor: '#FF3008' }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => rejectDoorDashOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div>
                            <div className="pos-order-meta-label">{providerLabel}</div>
                            <div className="pos-order-meta-value">{o?.posOrderId ? String(o.posOrderId) : (o?.razorpay_payment_id || o?.razorpay_ref || o?.phonepe_ref || o?.phonepe_merchant_order_id || '—')}</div>
                            {posOrderState ? <div className="field-hint">State: {posOrderState}</div> : null}
                          </div>

                          <div>
                            <div className="pos-order-meta-label">Set fulfillment</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OPEN')} disabled={busy || loadingMore || isCancelled}>
                                OPEN
                              </button>
                              {isDineIn ? (
                                <>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    READY
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'DINE_IN_COMPLETE')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    DINE-IN COMPLETE
                                  </button>
                                </>
                              ) : oIsDelivery ? (
                                <>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'READY_FOR_DELIVERY')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    READY
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'OUT_FOR_DELIVERY')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    OUT
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'DELIVERED')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    DELIVERED
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    READY
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'PICKUP_COMPLETE')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    PICKED UP
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                className="pos-chip"
                                onClick={() => updateFulfillment(o, 'CANCELLED')}
                                disabled={busy || loadingMore || isCancelled}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                CANCEL
                              </button>
                            </div>
                            {o?.squareFulfillmentSyncAttempted === true && o?.squareFulfillmentSyncOk === false && String(o?.squareFulfillmentSyncError || '').trim() ? (
                              <div className="field-hint" style={{ marginTop: 6 }}>
                                Square sync failed: {String(o.squareFulfillmentSyncError)}
                              </div>
                            ) : null}
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Notify</div>
                            <div className="pos-order-meta-value">email {emailStatus || 'n/a'}, sms {smsStatus || 'n/a'}</div>
                            {notifiedAt ? <div className="field-hint">Notified: {notifiedAt}</div> : null}
                          </div>
                        </div>

                        {paymentLinkUrl ? (
                          <div className="pos-order-links">
                            <a className="pos-link" href={paymentLinkUrl} target="_blank" rel="noreferrer">
                              Open payment link
                            </a>
                          </div>
                        ) : null}

                        <div className="pos-order-links">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => onPrintOrder(o, { silent: false, contentType: 'ticket' })}
                            disabled={busy || loadingMore}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Print Ticket
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => onPrintOrder(o, { silent: false, contentType: 'receipt' })}
                            disabled={busy || loadingMore}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Print Receipt
                          </button>
                        </div>

                        {/* Order additions (order-additions R8 / R4.8). All three controls
                            are presence-gated, so an order never added to renders exactly
                            as before. */}
                        {Array.isArray(o?.amendments) && o.amendments.length > 0 ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => onPrintAdditionChit(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Print added items (kitchen)
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Lists only the most recent addition. Do not reprint the whole
                              ticket — the kitchen already has it.
                            </div>
                          </div>
                        ) : null}

                        {isAddableOrder(o) && takeOrderCanEdit ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() =>
                                navigate(
                                  `/admin/channels/pos/take-order?addTo=${encodeURIComponent(
                                    String(o?.order_number || '')
                                  )}&at=${encodeURIComponent(String(o?.order_timestamp || ''))}`
                                )
                              }
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              + Add items
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Opens Take Order with this order selected.
                            </div>
                          </div>
                        ) : null}

                        {canTakePayment && squareAppButtonEnabledSetting ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => takePayment(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Square App
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Opens Square POS on this device for card-present or cash.
                            </div>
                          </div>
                        ) : null}

                        {canChargeOnSquareTerminal ? (
                          <div className="pos-order-links">
                            {terminalChargeActive ? (
                              <>
                                <div className="field-hint" style={{ marginTop: 10, fontWeight: 900, color: '#1f1142' }}>
                                  Waiting for the card on the Terminal
                                  {terminalCharge?.status ? ` · ${terminalCharge.status}` : ''}
                                </div>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={cancelTerminalCharge}
                                  style={{ width: '100%', marginTop: 10 }}
                                >
                                  Cancel Terminal checkout
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={() => chargeOnSquareTerminal(o)}
                                  disabled={busy || loadingMore || isCancelled || Boolean(terminalCharge)}
                                  style={{ width: '100%', marginTop: 10 }}
                                >
                                  {squareTerminalCompletion(o).label}
                                </button>
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  Wakes the paired Terminal and prompts the guest to tap, insert or
                                  swipe. The ticket flips to Paid when Square confirms the payment.
                                </div>
                              </>
                            )}
                          </div>
                        ) : null}

                        {canChargeOnCloverTerminal ? (
                          <div className="pos-order-links">
                            {cloverChargeActive ? (
                              <>
                                <div className="field-hint" style={{ marginTop: 10, fontWeight: 900, color: '#1f1142' }}>
                                  Waiting for the card on the Clover terminal
                                </div>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={cancelCloverTerminalCharge}
                                  style={{ width: '100%', marginTop: 10 }}
                                >
                                  Cancel Clover charge
                                </button>
                              </>
                            ) : (
                              renderCloverChargeButtons(o, isCancelled)
                            )}
                          </div>
                        ) : null}

                        {canChargeOnShift4Terminal ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => chargeOnShift4Terminal(o)}
                              disabled={busy || loadingMore || isCancelled || shift4Busy}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Charge on Shift4 terminal
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Pushes the card charge to the paired Shift4 terminal. Amount is
                              re-priced from this order.
                            </div>
                          </div>
                        ) : null}

                        {canCollectUpi ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => setUpiQrOrder(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Collect UPI (QR)
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Show a Razorpay UPI QR the customer scans with any UPI app. The
                              order is marked paid when Razorpay confirms the payment.
                            </div>
                          </div>
                        ) : null}

                        {canMarkAsPaid ? (
                          <div className="pos-order-links">
                            {isDineIn ? (
                              // Dine-in: settle + mark the ticket dine-in complete in one tap.
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => markPaidAndComplete(o, 'DINE_IN_COMPLETE', 'dine-in complete')}
                                disabled={busy || loadingMore || isCancelled}
                                style={{ width: '100%', marginTop: 10 }}
                              >
                                Cash paid + dine-in complete
                              </button>
                            ) : !oIsDelivery ? (
                              // Pickup (to-go / online pickup): settle + pickup complete.
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => markPaidAndComplete(o, 'PICKUP_COMPLETE', 'pickup complete')}
                                disabled={busy || loadingMore || isCancelled}
                                style={{ width: '100%', marginTop: 10 }}
                              >
                                Cash paid + pickup complete
                              </button>
                            ) : (
                              // Delivery: unchanged. Online delivery is paid online, so this
                              // rarely shows; when it does it stays a plain settle.
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => markAsPaid(o)}
                                disabled={busy || loadingMore}
                                style={{ width: '100%', marginTop: 10 }}
                              >
                                Record cash payment
                              </button>
                            )}
                          </div>
                        ) : null}

                        {canCancel ? (
                          <div className="pos-order-links">
                            <div className="field-hint" style={{ marginTop: 10 }}>
                              {posProvider === 'SQUARE'
                                ? 'Cancels the Square fulfillment (queue). Refunds are automatic only for paid-online orders.'
                                : providerLabel
                                  ? `Cancels the order in EzSell. Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`
                                  : 'Cancels the order in EzSell. No external POS action is associated with this order.'}
                            </div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => cancelOrder(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10, borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                            >
                              Cancel order
                            </button>
                          </div>
                        ) : (
                          <div className="pos-order-links">
                            <div className="field-hint" style={{ marginTop: 10 }}>
                              {isCancelled ? 'Order is cancelled.' : isPickupComplete ? 'Pickup complete — cancellation disabled.' : ''}
                            </div>
                          </div>
                        )}

                        {notes ? (
                          <div className="pos-order-notes">
                            <strong>Order Notes:</strong> {notes}
                          </div>
                        ) : null}

                        {Array.isArray(o?.customerSubmittedSnapshot?.items) && o.customerSubmittedSnapshot.items.length > 0 ? (
                          <div className="pos-order-items">
                            <div style={{ fontWeight: 900, color: '#0f172a' }}>Items</div>
                            <ul>
                              {o.customerSubmittedSnapshot.items.slice(0, 20).map((it, idx) => (
                                <li key={`${orderNumber}-item-${idx}`}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {String(it?.ImageURL || it?.image_url || it?.imageUrl || '').trim() ? (
                                      <img
                                        src={String(it?.ImageURL || it?.image_url || it?.imageUrl)}
                                        alt=""
                                        style={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: 10,
                                          objectFit: 'cover',
                                          border: '1px solid rgba(15, 23, 42, 0.10)',
                                        }}
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        onError={(e) => {
                                          try {
                                            e.currentTarget.style.display = 'none';
                                          } catch {
                                            // ignore
                                          }
                                        }}
                                      />
                                    ) : null}
                                    <span style={{ minWidth: 0 }}>
                                      <span>
                                        {posItemNameText(it)} x{String(it?.Quantity || it?.quantity || 1)}
                                      </span>
                                      {String(it?.item_notes || it?.itemNotes || '').trim() ? (
                                        <div className="pos-item-notes-highlight">
                                          <strong>Item Notes:</strong> {String(it?.item_notes || it?.itemNotes || '').trim()}
                                        </div>
                                      ) : null}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {detailsOrder ? (
        <div
          className="fulfillment-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetailsOrder(null);
          }}
        >
          <div className="fulfillment-modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="fulfillment-modal-header">
              <h3>Order {String(detailsOrder?.order_number || '—')}</h3>
              <button type="button" className="admin-mini-btn" onClick={() => onPrintOrder(detailsOrder, { silent: false, contentType: 'ticket' })} disabled={busy}>
                Print Ticket
              </button>
              <button type="button" className="admin-mini-btn" onClick={() => onPrintOrder(detailsOrder, { silent: false, contentType: 'receipt' })} disabled={busy}>
                Print Receipt
              </button>
              <button type="button" className="admin-mini-btn" onClick={() => setDetailsOrder(null)} disabled={busy}>
                Close
              </button>
            </div>
            <div className="fulfillment-modal-body">
              {(() => {
                const o = detailsOrder;
                const ownTaxEnabled =
                  Boolean(o?.posTaxEnabled ?? posTaxEnabled) &&
                  String(o?.posTaxCollectionMode ?? posTaxCollectionMode ?? 'PROVIDER')
                    .trim()
                    .toUpperCase() === 'OWN';
                const ratePercent = Number(o?.posTaxRatePercent ?? posTaxRatePercent);

                const orderSubtotalCents = safeInt(o?.subtotal_cents, 0);
                const orderTaxCents = safeInt(o?.tax_cents, 0);
                const hasStoredBreakdown = Number.isFinite(Number(o?.subtotal_cents)) && Number.isFinite(Number(o?.tax_cents));

                const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(o?.amount_total_cents, 0);
                const rawTotalCents = safeInt(o?.amount_total_cents, 0);
                const taxFromStored = hasStoredBreakdown ? orderTaxCents : 0;
                const inclusive = isTaxInclusiveOrder(o);
                const discountCents = resolveDiscountCents({
                  order: o,
                  subtotalCents,
                  taxCents: taxFromStored,
                  rawTotalCents,
                  hasStoredBreakdown,
                });
                const taxableSubtotalCents = Math.max(0, subtotalCents - discountCents);
                const { taxCents: computedTaxCents, totalCents: computedTotalCents } = ownTaxEnabled
                  ? computeOwnTax(taxableSubtotalCents, ratePercent)
                  : { taxCents: 0, totalCents: taxableSubtotalCents };

                const taxCents = hasStoredBreakdown ? taxFromStored : computedTaxCents;
                // Inclusive orders already have tax inside the subtotal, so the
                // total is the discounted subtotal on its own.
                const totalCents = hasStoredBreakdown
                  ? (inclusive ? taxableSubtotalCents : taxableSubtotalCents + taxCents)
                  : computedTotalCents;

                const total = formatMoney(o?.posFinalTotalMoney, o?.amount_total_cents);
                const placedAt = formatDateTimeInZone(o?.order_timestamp);
                const oIsScheduled = o?.isScheduled === true && Number.isFinite(Number(o?.scheduledTime));
                const scheduledAt = oIsScheduled ? formatDateTimeInZone(o.scheduledTime) : '';
                const customerName = String(o?.customer_name || '').trim();
                const customerEmail = String(o?.customer_email || '').trim();
                const customerPhone = String(o?.customer_phone || '').trim();
                const notes = String(o?.customerSubmittedSnapshot?.notes || '').trim();
                const paymentStatus = String(o?.payment_status || 'unknown').trim();
                const paymentMode = String(o?.posPaymentMode || '').trim();
                const posProvider = String(o?.posProvider || '').trim().toUpperCase();
                const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : posProvider === 'PHONEPE' ? 'PhonePe' : posProvider === 'RAZORPAY' ? 'Razorpay' : '';
                const posOrderState = String(o?.posOrderState || o?.squareOrderState || '').trim();
                const rawFulfillment = String(o?.adminFulfillmentStatus || o?.posFulfillmentState || o?.squareFulfillmentState || '').trim();
                const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
                const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
                const paymentLinkUrl = String(o?.posPaymentLink?.href || o?.posPaymentLink?.long_url || o?.posPaymentLink?.url || '').trim();
                const notifyEmailStatus = String(o?.posPaidNotifyResult?.email || '').trim();
                const notifySmsStatus = String(o?.posPaidNotifyResult?.sms || '').trim();
                const notifiedAt = o?.posPaidNotifiedAt ? formatDateTimeInZone(o.posPaidNotifiedAt) : '';
                const items = Array.isArray(o?.customerSubmittedSnapshot?.items) ? o.customerSubmittedSnapshot.items : [];

                const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
                const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED';
                const isPickupComplete = String(fulfillmentLabel || '').trim().toUpperCase() === 'PICKUP COMPLETE';
                const canCancel = !isCancelled && !isPickupComplete;

                const isUberEatsOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'ubereats' ||
                  String(o?.marketplaceProvider || '').trim().toUpperCase() === 'UBEREATS';
                const canAcceptUberEats =
                  isUberEatsOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');

                const isDoorDashOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'doordash' ||
                  String(o?.marketplaceProvider || '').trim().toUpperCase() === 'DOORDASH';
                const canAcceptDoorDash =
                  isDoorDashOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');
                const experienceFlag = String(o?.experience_flag || '').trim();

                const paymentModeUpper = String(o?.posPaymentMode || '').trim().toUpperCase();
                const canTakePayment =
                  posProvider === 'SQUARE' && paymentModeUpper === 'PAY_AT_POS' && paymentStatus.toLowerCase() === 'pending_payment';
                // Card-present via the Terminal API (same gate as the grouped list above).
                const canChargeOnSquareTerminal =
                  canTakePayment && squareTerminalPaired && Boolean(String(o?.posOrderId || '').trim());
                const terminalChargeActive = Boolean(terminalCharge) && terminalCharge?.key === orderKey(o);
                // CLOVER Terminal Charge (same gate as the grouped list above).
                const canChargeOnCloverTerminal =
                  posProvider === 'CLOVER'
                  && paymentModeUpper === 'PAY_AT_POS'
                  && paymentStatus.toLowerCase() === 'pending_payment'
                  && cloverTerminalPaired
                  && Boolean(String(o?.posOrderId || o?.cloverOrderId || '').trim());
                const cloverChargeActive = Boolean(cloverTerminalCharge) && cloverTerminalCharge?.key === orderKey(o);
                // SHIFT4 Terminal Charge (same gate as the grouped list above). IN-STORE /
                // staff tickets carry no posProvider, so fall back to the tenant provider.
                const effectiveProviderShift4 =
                  posProvider || posTenantProvider || posTenantProviderRef.current;
                const canChargeOnShift4Terminal =
                  effectiveProviderShift4 === 'SHIFT4'
                  && paymentModeUpper === 'PAY_AT_POS'
                  && paymentStatus.toLowerCase() === 'pending_payment'
                  && shift4TerminalPaired;
                const shouldAutoRefund = posProvider === 'SQUARE' && (paymentModeUpper === 'PAY_NOW' || !!paymentLinkUrl);
                const canMarkPaid =
                  !isCancelled && paymentStatus.toLowerCase() === 'pending_payment';
                // In-app card payment (in-app-card-payment spec). Pure gate mirroring the
                // mobile copy: Stripe + PAY_AT_POS (pay-at-store, arrives unpaid) +
                // pending_payment + not cancelled + tenant opted in. This is the order
                // collected at the counter; a PAY_NOW order already settled on the hosted
                // page and has nothing to charge. Additive to the settle buttons below.
                const canChargeCard = canChargeCardInApp(o, {
                  inAppCardPaymentEnabled: inAppCardEnabled || inAppCardEnabledRef.current,
                  // Tenant provider fallback so an IN-STORE / staff ticket (which carries
                  // no posProvider of its own) still resolves Stripe.
                  posProvider: posTenantProvider || posTenantProviderRef.current,
                });
                // Razorpay UPI at-store QR (razorpay-upi-dinein-qr spec). Same gate shape as
                // canChargeCard but keyed on the SEPARATE razorpayUpiAtStoreEnabled flag and
                // requiring a RAZORPAY + INR unpaid order. Same ref-OR + tenant-provider
                // fallback (an in-store ticket carries no posProvider of its own).
                const canCollectUpi = canCollectUpiAtStore(o, {
                  razorpayUpiAtStoreEnabled: razorpayUpiEnabled || razorpayUpiEnabledRef.current,
                  posProvider: posTenantProvider || posTenantProviderRef.current,
                });
                // Dine-in detected off orderType (dine-in tickets are fulfillment_type
                // "pickup"). Drives the dine-in chip set + combined completion button.
                const orderTypeRaw = String(o?.orderType || o?.customerSubmittedSnapshot?.orderType || '').trim().toUpperCase();
                const isDineIn = orderTypeRaw === 'DINE_IN';

                return (
                  <>
                    <div className="pos-order-meta" style={{ marginTop: 0 }}>
                      <div>
                        <div className="pos-order-meta-label">Placed</div>
                        <div className="pos-order-meta-value">{placedAt}</div>
                      </div>
                      {oIsScheduled ? (
                        <div>
                          <div className="pos-order-meta-label">Scheduled for</div>
                          <div className="pos-order-meta-value" style={{ fontWeight: 700, color: '#4c1d95' }}>{scheduledAt}</div>
                        </div>
                      ) : null}
                      {isUberEatsOrder ? (
                        <div style={{ gridColumn: '1 / -1', padding: 12, backgroundColor: '#E9FBF1', borderRadius: 8, border: '1px solid #06C167' }}>
                          <div style={{ fontWeight: 700, color: '#067D43', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🛵</span>
                            <span>Uber Eats Delivery Order</span>
                          </div>
                          {o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                            </div>
                          ) : null}
                          {o?.delivery_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Instructions:</strong> {o.delivery_instructions}
                            </div>
                          ) : null}
                          {o?.requested_delivery_time ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <strong>Requested:</strong> {formatDateTimeInZone(o.requested_delivery_time)}
                            </div>
                          ) : null}
                          {o?.ubereats_order_id ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                              Uber Eats Order ID: {o.ubereats_order_id}
                            </div>
                          ) : null}
                          {canAcceptUberEats ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => acceptUberEatsOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ backgroundColor: '#06C167', borderColor: '#06C167' }}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => rejectUberEatsOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {isDoorDashOrder ? (
                        <div style={{ gridColumn: '1 / -1', padding: 12, backgroundColor: '#FFF0ED', borderRadius: 8, border: '1px solid #FF3008' }}>
                          <div style={{ fontWeight: 700, color: '#B3210A', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🚗</span>
                            <span>DoorDash Delivery Order</span>
                            {experienceFlag ? (
                              <span className="pos-badge pos-badge-neutral" style={{ marginLeft: 4 }}>{experienceFlag}</span>
                            ) : null}
                          </div>
                          {o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                            </div>
                          ) : null}
                          {o?.special_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Special Instructions:</strong> {o.special_instructions}
                            </div>
                          ) : null}
                          {o?.delivery_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Instructions:</strong> {o.delivery_instructions}
                            </div>
                          ) : null}
                          {o?.requested_delivery_time ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <strong>Requested:</strong> {formatDateTimeInZone(o.requested_delivery_time)}
                            </div>
                          ) : null}
                          {o?.merchant_tip > 0 ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#059669' }}>
                              <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                              <strong>Merchant Tip:</strong> {formatMoney(null, o.merchant_tip)}
                            </div>
                          ) : null}
                          {o?.doordash_order_id ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                              DoorDash Order ID: {o.doordash_order_id}
                            </div>
                          ) : null}
                          {canAcceptDoorDash ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => acceptDoorDashOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ backgroundColor: '#FF3008', borderColor: '#FF3008' }}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => rejectDoorDashOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div>
                        <div className="pos-order-meta-label">Payment</div>
                        <div className="pos-order-meta-value">{paymentStatus || 'unknown'}</div>
                        {paymentMode ? <div className="field-hint">Mode: {paymentMode}</div> : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Fulfillment</div>
                        <div className="pos-order-meta-value">{fulfillmentLabel || '—'}</div>
                        {o?.squareFulfillmentSyncAttempted === true && o?.squareFulfillmentSyncOk === false && String(o?.squareFulfillmentSyncError || '').trim() ? (
                          <div className="field-hint">Square sync failed: {String(o.squareFulfillmentSyncError)}</div>
                        ) : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Total</div>
                        <div className={`pos-order-meta-value${ownTaxEnabled ? ' pos-order-total-highlight' : ''}`}>{total}</div>
                        {(() => {
                          // Show the tax line whenever the server stamped a tax, not only
                          // when the order row echoes the ownTax flags. See the collapsed
                          // card above for why gating on ownTaxEnabled hid stored tax.
                          const showStoredTax = hasStoredBreakdown && taxCents > 0;
                          if (!(showStoredTax || ownTaxEnabled || discountCents)) return null;
                          const taxLabel = `${resolveTaxLabel(o)}${inclusive ? ' (incl.)' : ''}`;
                          return (
                            <div className="pos-order-total-breakdown">
                              <div className="pos-order-total-row">
                                <span>Subtotal</span>
                                <span>{formatMoney(null, subtotalCents)}</span>
                              </div>
                              {discountCents ? (
                                <div className="pos-order-total-row">
                                  <span>Discount</span>
                                  <span>-{formatMoney(null, discountCents)}</span>
                                </div>
                              ) : null}
                              {(showStoredTax || ownTaxEnabled) ? (
                                <div className="pos-order-total-row">
                                  <span>{taxLabel}{ownTaxEnabled && !showStoredTax ? ` (${Number(ratePercent) || 0}%)` : ''}</span>
                                  <span>{formatMoney(null, taxCents)}</span>
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Customer</div>
                        <div className="pos-order-meta-value">{customerName || '—'}</div>
                        {customerPhone ? <div className="field-hint">Phone: {maskPhone(customerPhone)}</div> : null}
                        {customerEmail ? <div className="field-hint">Email: {customerEmail}</div> : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">{providerLabel}</div>
                        <div className="pos-order-meta-value">{o?.posOrderId ? String(o.posOrderId) : (o?.razorpay_payment_id || o?.razorpay_ref || o?.phonepe_ref || o?.phonepe_merchant_order_id || '—')}</div>
                        {posOrderState ? <div className="field-hint">State: {posOrderState}</div> : null}
                      </div>

                      <div>
                        <div className="pos-order-meta-label">Set fulfillment</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                          <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OPEN')} disabled={busy || loadingMore || isCancelled}>
                            OPEN
                          </button>
                          {isDineIn ? (
                            <>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')} disabled={busy || loadingMore || isCancelled}>
                                READY
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'DINE_IN_COMPLETE')} disabled={busy || loadingMore || isCancelled}>
                                DINE-IN COMPLETE
                              </button>
                            </>
                          ) : oIsDelivery ? (
                            <>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'READY_FOR_DELIVERY')} disabled={busy || loadingMore || isCancelled}>
                                READY
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OUT_FOR_DELIVERY')} disabled={busy || loadingMore || isCancelled}>
                                OUT
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'DELIVERED')} disabled={busy || loadingMore || isCancelled}>
                                DELIVERED
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')} disabled={busy || loadingMore || isCancelled}>
                                READY
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'PICKUP_COMPLETE')} disabled={busy || loadingMore || isCancelled}>
                                PICKED UP
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="pos-chip"
                            onClick={() => updateFulfillment(o, 'CANCELLED')}
                            disabled={busy || loadingMore || isCancelled}
                            style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                          >
                            CANCEL
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Notify</div>
                        <div className="pos-order-meta-value">email {notifyEmailStatus || 'n/a'}, sms {notifySmsStatus || 'n/a'}</div>
                        {notifiedAt ? <div className="field-hint">Notified: {notifiedAt}</div> : null}
                      </div>
                    </div>

                    {paymentLinkUrl ? (
                      <div className="pos-order-links">
                        <a className="pos-link" href={paymentLinkUrl} target="_blank" rel="noreferrer">
                          Open payment link
                        </a>
                      </div>
                    ) : null}

                    {notes ? (
                      <div className="pos-order-notes">
                        <strong>Order Notes:</strong> {notes}
                      </div>
                    ) : null}

                    {items.length > 0 ? (
                      <div className="pos-order-items">
                        <div style={{ fontWeight: 900, color: '#0f172a' }}>Items</div>
                        <ul>
                          {items.slice(0, 20).map((it, idx) => {
                            const imageUrl = String(it?.ImageURL || it?.image_url || it?.imageUrl || '').trim();
                            const name = posItemNameText(it);
                            const qty = String(it?.Quantity || it?.quantity || 1);
                            return (
                              <li key={`${String(detailsOrder?.order_number || '')}-item-${idx}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt=""
                                      style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        objectFit: 'cover',
                                        border: '1px solid rgba(15, 23, 42, 0.10)',
                                      }}
                                      loading="lazy"
                                      referrerPolicy="no-referrer"
                                      onError={(e) => {
                                        try {
                                          e.currentTarget.style.display = 'none';
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <span style={{ minWidth: 0 }}>
                                    <span>
                                      {name} x{qty}
                                    </span>
                                    {String(it?.item_notes || it?.itemNotes || '').trim() ? (
                                      <div className="pos-item-notes-highlight">
                                        <strong>Item Notes:</strong> {String(it?.item_notes || it?.itemNotes || '').trim()}
                                      </div>
                                    ) : null}
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {canChargeCard ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => setChargeCardOrder(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Charge card
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Collect this pay-at-store order by card, Apple Pay or Google Pay
                          via Stripe. Wallet buttons appear when this browser supports them.
                        </div>
                      </div>
                    ) : null}

                    {canCollectUpi ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => setUpiQrOrder(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Collect UPI (QR)
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Show a Razorpay UPI QR the customer scans with any UPI app. The
                          order is marked paid when Razorpay confirms the payment.
                        </div>
                      </div>
                    ) : null}

                    {canTakePayment && squareAppButtonEnabledSetting ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => takePayment(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Square App
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Opens Square POS on this device for card-present or cash.
                        </div>
                      </div>
                    ) : null}

                    {canChargeOnSquareTerminal ? (
                      <div className="pos-order-links">
                        {terminalChargeActive ? (
                          <>
                            <div className="field-hint" style={{ marginTop: 10, fontWeight: 900, color: '#1f1142' }}>
                              Waiting for the card on the Terminal
                              {terminalCharge?.status ? ` · ${terminalCharge.status}` : ''}
                            </div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={cancelTerminalCharge}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Cancel Terminal checkout
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => chargeOnSquareTerminal(o)}
                              disabled={busy || loadingMore || isCancelled || Boolean(terminalCharge)}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              {squareTerminalCompletion(o).label}
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Wakes the paired Terminal and prompts the guest to tap, insert or
                              swipe. The ticket flips to Paid when Square confirms the payment.
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    {canChargeOnCloverTerminal ? (
                      <div className="pos-order-links">
                        {cloverChargeActive ? (
                          <>
                            <div className="field-hint" style={{ marginTop: 10, fontWeight: 900, color: '#1f1142' }}>
                              Waiting for the card on the Clover terminal
                            </div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={cancelCloverTerminalCharge}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Cancel Clover charge
                            </button>
                          </>
                        ) : (
                          renderCloverChargeButtons(o, isCancelled)
                        )}
                      </div>
                    ) : null}

                    {canChargeOnShift4Terminal ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => chargeOnShift4Terminal(o)}
                          disabled={busy || loadingMore || isCancelled || shift4Busy}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Charge on Shift4 terminal
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Pushes the card charge to the paired Shift4 terminal. Amount is
                          re-priced from this order.
                        </div>
                      </div>
                    ) : null}

                    {canMarkPaid ? (
                      <div className="pos-order-links">
                        {isDineIn ? (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => markPaidAndComplete(o, 'DINE_IN_COMPLETE', 'dine-in complete')}
                            disabled={busy || loadingMore || isCancelled}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Cash paid + dine-in complete
                          </button>
                        ) : !oIsDelivery ? (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => markPaidAndComplete(o, 'PICKUP_COMPLETE', 'pickup complete')}
                            disabled={busy || loadingMore || isCancelled}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Cash paid + pickup complete
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => markAsPaid(o)}
                            disabled={busy || loadingMore}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Record cash payment
                          </button>
                        )}
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Records the order as paid in cash for the full amount.
                        </div>
                      </div>
                    ) : null}

                    {canCancel ? (
                      <div className="pos-order-links">
                        <div className="field-hint" style={{ marginTop: 10 }}>
                          {posProvider === 'SQUARE'
                            ? shouldAutoRefund
                              ? 'Cancels the Square fulfillment (queue) and attempts full refund if paid online.'
                              : 'Cancels the Square fulfillment (queue). Refunds for in-person payments must be done in Square POS.'
                            : providerLabel
                              ? `Cancels the order in EzSell. Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`
                              : 'Cancels the order in EzSell. No external POS action is associated with this order.'}
                        </div>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => cancelOrder(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10, borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                        >
                          Cancel order
                        </button>
                      </div>
                    ) : (
                      <div className="pos-order-links">
                        <div className="field-hint" style={{ marginTop: 10 }}>
                          {isCancelled ? 'Order is cancelled.' : isPickupComplete ? 'Pickup complete — cancellation disabled.' : ''}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
